/**
 * DefencIQ Report Generator
 * Generates structured assessment reports aligned to the canonical schema
 * Includes executive summary, domain findings, gating failures, stack recommendations, priority gaps, and roadmap
 */

import { calculateAssessmentScores, getDomainMaturityBand, getAnswerScore, buildStackAnalysis } from './scoring-engine';
import { buildHybridReport } from './hybrid-report';
import { computeEvidenceReadiness } from './evidence-readiness';
import { 
  getStackRecommendation, 
  isGreenfieldCandidate, 
  isManagedUpliftCandidate,
  determineServiceTier,
  getServiceTierInfo,
  determineEnvironmentType,
  getEnvironmentTypeInfo,
  getComponentLabel
} from './stack-mapper';
import { SECTION_CONFIG, DOMAIN_WEIGHTS } from '@/config/domain-weights';
import { QUESTION_CONFIG, getQuestionByFieldId, SCORED_SECTIONS } from '@/config/question-config';

/**
 * Derive DISP Submission Readiness fields from enhanced scoring data.
 * These are report-layer calculations only - no changes to the scoring engine.
 * @param {Object} enhancedScores - From calculateEnhancedScores
 * @param {Object} gatingStatus - From calculateAssessmentScores
 * @param {Object} answers - All assessment answers (for partial control detection)
 * @returns {Object} DISP readiness fields
 */
export const deriveDISPReadiness = (enhancedScores, gatingStatus, answers = {}) => {
  const finalScore = enhancedScores?.finalScore ?? 0;
  const evidenceMultiplier = enhancedScores?.multipliers?.evidence ?? 1;
  const policyEnforcementMultiplier = enhancedScores?.multipliers?.policyEnforcement ?? 1;
  const unknownCount = enhancedScores?.multipliers?.unknownCount ?? 0;
  const gatingFailures = gatingStatus?.score0Count ?? 0;

  // Detect partial control implementations
  const hasPartialControls = Object.values(answers).some(
    a => a?.status === 'partial' || a?.answer === 'partial' || a?.answer === 'partial_implementation'
  );

  // 1.1 DISP Submission Readiness Status
  let submission_status;
  if (finalScore < 75 || gatingFailures > 0 || evidenceMultiplier < 0.85) {
    submission_status = 'NOT_READY';
  } else if (finalScore <= 84) {
    submission_status = 'PARTIALLY_READY';
  } else {
    submission_status = 'READY_FOR_SUBMISSION';
  }

  // 1.2 Evidence Readiness Level
  let evidence_readiness;
  if (evidenceMultiplier >= 0.95) evidence_readiness = 'HIGH';
  else if (evidenceMultiplier >= 0.85) evidence_readiness = 'MODERATE';
  else if (evidenceMultiplier >= 0.70) evidence_readiness = 'LOW';
  else evidence_readiness = 'CRITICAL';

  // 1.3 Estimated Uplift Effort
  let estimated_effort;
  if (finalScore > 85) estimated_effort = '2-4 weeks';
  else if (finalScore > 70) estimated_effort = '4-8 weeks';
  else if (finalScore >= 50) estimated_effort = '6-10 weeks';
  else estimated_effort = '8-12 weeks';

  // 1.4 Key Submission Risks (detailed, conversion-optimised messaging)
  const submission_risks = [];
  if (gatingFailures > 0) submission_risks.push('Critical control gaps will result in CSQ failure');
  if (policyEnforcementMultiplier < 0.9) submission_risks.push('Inconsistent policy enforcement reduces audit confidence');
  if (evidenceMultiplier < 0.9) submission_risks.push('Evidence is not centrally structured or readily defensible');
  if (hasPartialControls) submission_risks.push('Partial implementations may trigger Defence clarification requests');
  if (unknownCount >= 3) submission_risks.push('Low confidence in responses undermines submission credibility');

  return {
    submission_status,
    evidence_readiness,
    estimated_effort,
    submission_risks,
  };
};

/**
 * Generate priority gaps from question scores
 * Gap creation rules:
 * - Create a gap if score = 0, or if score = 1 and criticality is critical or high
 * Priority rules:
 * - criticality = critical and score = 0 => priority = critical
 * - criticality = critical and score = 1 => priority = high
 * - criticality = high and score = 0 => priority = high
 * - criticality = high and score = 1 => priority = medium
 * - else => low or medium as appropriate
 * @param {Object} answers - All answers
 * @returns {Array} Priority gaps
 */
export const generatePriorityGaps = (answers) => {
  const gaps = [];

  Object.entries(QUESTION_CONFIG).forEach(([sectionId, section]) => {
    section.questions.forEach(question => {
      if (question.weight === 0) return; // Skip non-scored

      const answer = answers[question.field_id];
      const score = getAnswerScore(answer, question);
      const { criticality } = question;

      // Determine if this is a gap
      const isGap = score === 0 || (score === 1 && (criticality === 'critical' || criticality === 'high'));
      if (!isGap) return;

      // Determine priority
      let priority = 'low';
      if (criticality === 'critical' && score === 0) {
        priority = 'critical';
      } else if (criticality === 'critical' && score === 1) {
        priority = 'high';
      } else if (criticality === 'high' && score === 0) {
        priority = 'high';
      } else if (criticality === 'high' && score === 1) {
        priority = 'medium';
      } else if (criticality === 'medium' && score === 0) {
        priority = 'medium';
      }

      gaps.push({
        domain: sectionId,
        domain_label: SECTION_CONFIG[sectionId]?.title || sectionId,
        question_id: question.question_id,
        field_id: question.field_id,
        gap: question.prompt,
        score,
        criticality,
        priority,
        recommended_components: question.recommended_components || [],
        recommended_actions: question.recommended_actions || []
      });
    });
  });

  // Sort by priority
  const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  return gaps.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
};

/**
 * Generate three-phase remediation roadmap
 * Phases:
 * - Phase 1 - Critical Foundations (critical gating controls)
 * - Phase 2 - ML2 Uplift (high priority gaps)
 * - Phase 3 - Continuous Compliance (medium/low priority items)
 * @param {Array} priorityGaps - Sorted priority gaps
 * @returns {Object} Three-phase roadmap
 */
export const generateRoadmap = (priorityGaps) => {
  return {
    phase_1: {
      name: 'Phase 1 - Critical Foundations',
      description: 'Address critical gating controls to establish security baseline',
      items: priorityGaps.filter(g => g.priority === 'critical')
    },
    phase_2: {
      name: 'Phase 2 - ML2 Uplift',
      description: 'Implement high priority controls for Essential Eight ML2 alignment',
      items: priorityGaps.filter(g => g.priority === 'high')
    },
    phase_3: {
      name: 'Phase 3 - Continuous Compliance',
      description: 'Complete remaining items and establish ongoing compliance monitoring',
      items: priorityGaps.filter(g => g.priority === 'medium' || g.priority === 'low')
    }
  };
};

/**
 * Generate domain-by-domain findings
 * @param {Object} domainScores - All domain scores
 * @param {Object} answers - All answers
 * @returns {Array} Domain findings
 */
export const generateDomainFindings = (domainScores, answers) => {
  return SCORED_SECTIONS.map(sectionId => {
    const score = domainScores[sectionId];
    const config = SECTION_CONFIG[sectionId];
    const weight = DOMAIN_WEIGHTS[sectionId];
    const maturity = getDomainMaturityBand(score?.percentage || 0);

    // Get question-level details
    const section = QUESTION_CONFIG[sectionId];
    const questionFindings = section?.questions
      .filter(q => q.weight > 0)
      .map(q => {
        const answer = answers[q.field_id];
        const qScore = getAnswerScore(answer, q);
        return {
          question_id: q.question_id,
          field_id: q.field_id,
          prompt: q.prompt,
          score: qScore,
          status: answer?.status || 'unanswered',
          criticality: q.criticality,
          gating_rule: q.gating_rule
        };
      }) || [];

    return {
      domain_id: sectionId,
      domain_name: config?.title || sectionId,
      weight,
      score: score?.percentage || 0,
      maturity,
      answered: score?.answered || 0,
      total: score?.total || 0,
      questions: questionFindings
    };
  });
};

/**
 * Generate executive summary text
 * @param {Object} reportData - Full report data
 * @returns {string} Executive summary
 */
export const generateExecutiveSummary = (reportData) => {
  const {
    engagement,
    overallScore,
    overallStatus,
    serviceTier,
    gatingStatus,
    stackRecommendation,
    priorityGaps
  } = reportData;

  const tierInfo = getServiceTierInfo(serviceTier);
  const criticalGaps = priorityGaps.filter(g => g.priority === 'critical').length;
  const highGaps = priorityGaps.filter(g => g.priority === 'high').length;

  return `
DEFENCIQ DISCOVERY ASSESSMENT
=============================

Client: ${engagement.client_name || 'Not specified'}
Assessment Date: ${engagement.assessment_date || new Date().toISOString().split('T')[0]}
Assessment ID: ${engagement.assessment_id || 'DIQ-' + Date.now()}
Assessor: ${engagement.assessor_name || 'Not specified'}

EXECUTIVE SUMMARY
-----------------
Overall E8 ML2 Readiness Score: ${overallScore}%
Status: ${overallStatus.replace(/_/g, ' ').toUpperCase()}
Recommended Service Tier: ${tierInfo.label}

Gating Controls: ${gatingStatus.score2Count}/${gatingStatus.total} passed
${gatingStatus.allPassed ? '[OK] All critical gating controls satisfied' : `[!] ${gatingStatus.score0Count + gatingStatus.score1Count} gating controls require attention`}

IDENTIFIED GAPS
---------------
- Critical: ${criticalGaps}
- High Priority: ${highGaps}
- Total: ${priorityGaps.length}

RECOMMENDED STACK COMPONENTS
----------------------------
${stackRecommendation.components.map(c => `- ${c.label}`).join('\n')}

${reportData.greenfieldCandidate 
  ? 'This engagement is classified as GREENFIELD BUILD requiring full security stack implementation.'
  : reportData.managedUpliftCandidate
  ? 'This engagement is classified as MANAGED UPLIFT with existing foundations to build upon.'
  : 'This engagement requires targeted assessment and remediation planning.'}
`.trim();
};

/**
 * Generate CEO-friendly Executive Brief from existing results
 */
const generateExecutiveBrief = (data) => {
  const {
    weightedScore, scoreBand, overallScore, environmentType, environmentTypeInfo,
    serviceTier, serviceTierInfo, domainFindings, priorityGaps, gatingStatus,
    dispReadiness, stackAnalysis, auditFailureLikelihood, auditFailureMessage
  } = data;

  // Section 1: Readiness Summary
  const readinessBand = scoreBand?.label || 'Unknown';
  const isReady = weightedScore >= 80;
  const isApproaching = weightedScore >= 55;

  let timeToReadiness;
  if (weightedScore >= 80) timeToReadiness = 'Ready or near-ready';
  else if (weightedScore >= 55) timeToReadiness = '3-6 weeks';
  else timeToReadiness = '6-10 weeks';

  const readiness_summary = {
    readiness_status: isReady
      ? 'Approaching DISP readiness'
      : isApproaching
        ? 'Approaching DISP readiness - gaps remain'
        : 'Not ready for DISP submission',
    ml2_score: `${weightedScore}% (${readinessBand})`,
    environment_summary: environmentTypeInfo?.label || environmentType || 'Unknown',
    time_to_readiness: timeToReadiness,
    recommended_service: serviceTierInfo?.label || 'Snapshot Assessment'
  };

  // Section 2: Risk Summary
  const riskExplanations = {
    CRITICAL: 'Very high likelihood of failing a DISP assessment',
    HIGH: 'High likelihood of failing without remediation',
    MODERATE: 'Some gaps may delay approval',
    LOW: 'Low risk if controls are maintained'
  };

  const risk_summary = {
    audit_risk: auditFailureLikelihood || 'Unknown',
    risk_explanation: riskExplanations[auditFailureLikelihood] || 'Risk level could not be determined',
    consequences: [
      'Risk of CSQ rejection or follow-up scrutiny',
      'Potential delays to Defence contract eligibility',
      'Increased scrutiny during assessment'
    ]
  };

  // Section 3: Key Gaps (top 5, critical + high only, plain English)
  const gapTranslations = {
    mfa_all_users: 'Multi-factor authentication is not enforced for all users',
    mfa_privileged_users: 'Privileged user accounts are not fully protected with MFA',
    allow_listing_in_place: 'Application control is not fully enforced',
    restrict_which_apps_can_run: 'Users can run unapproved applications',
    centralised_logging: 'Security monitoring is incomplete - no centralised logging',
    alerts_actively_monitored: 'Security alerts are not actively monitored',
    admin_activity_logged_and_reviewed: 'Administrator activity is not being tracked',
    separate_admin_accounts: 'Separate admin accounts are not in use',
    temporary_privilege_elevation: 'Permanent elevated access exists without just-in-time controls',
    offline_or_immutable_backups: 'Backups are not adequately protected against tampering',
    last_successful_restore_test: 'Backup recovery has not been tested recently',
    device_compliance_policies: 'Device compliance policies are not enforced',
    os_patch_management_method: 'Operating system patching is not centrally managed',
    vulnerability_scanning_used: 'No vulnerability scanning is in place',
    conditional_access: 'Context-aware access policies are not configured',
    remote_config_or_wipe: 'Devices cannot be remotely managed or wiped if compromised',
    application_execution_monitored: 'Application execution is not monitored',
    users_can_install_software: 'Users can install software without restriction'
  };

  const criticalHighGaps = priorityGaps
    .filter(g => g.priority === 'critical' || g.priority === 'high')
    .slice(0, 5);

  const key_gaps = criticalHighGaps.map(g => {
    const fieldId = g.gap?.split('?')[0]?.trim();
    // Try to match from translations, fall back to gap text
    for (const [key, translation] of Object.entries(gapTranslations)) {
      if (g.gap?.toLowerCase().includes(key.replace(/_/g, ' ')) || g.field_id === key) {
        return translation;
      }
    }
    // Clean the gap text into plain English
    return g.gap?.replace(/^Is\s+/i, '').replace(/\?$/, '') + ' is not in place';
  });

  // Section 4: Action Plan (3 priority groups)
  const gatingFailures = gatingStatus?.controls?.filter(c => !c.passed) || [];
  const highGaps = priorityGaps.filter(g => g.priority === 'critical' || g.priority === 'high');
  const governanceGaps = priorityGaps.filter(g =>
    g.domain === 'compliance_and_governance' || g.domain === 'policy_and_governance' ||
    g.gap?.toLowerCase().includes('evidence') || g.gap?.toLowerCase().includes('compliance')
  );

  const action_plan = {
    priority_1: {
      title: 'Critical - Must Fix First',
      items: gatingFailures.slice(0, 5).map(c => {
        const prompt = c.prompt || '';
        // Translate to action-oriented plain English
        if (prompt.toLowerCase().includes('mfa') && prompt.toLowerCase().includes('privileged')) return 'Enforce MFA for all privileged users';
        if (prompt.toLowerCase().includes('mfa') && prompt.toLowerCase().includes('all')) return 'Enforce MFA for all users';
        if (prompt.toLowerCase().includes('allow-listing') || prompt.toLowerCase().includes('allow listing')) return 'Strengthen application control enforcement';
        if (prompt.toLowerCase().includes('logging')) return 'Implement centralised security logging';
        if (prompt.toLowerCase().includes('backup')) return 'Ensure backups are immutable and regularly tested';
        if (prompt.toLowerCase().includes('admin') && prompt.toLowerCase().includes('separate')) return 'Create separate administrator accounts';
        if (prompt.toLowerCase().includes('patch')) return 'Centralise and enforce patch management';
        if (prompt.toLowerCase().includes('vulnerability')) return 'Deploy vulnerability scanning';
        return prompt.replace(/^Is\s+/i, 'Implement ').replace(/\?$/, '');
      })
    },
    priority_2: {
      title: 'Required for ML2',
      items: highGaps
        .filter(g => !gatingFailures.some(gf => gf.field_id === g.field_id))
        .slice(0, 5)
        .map(g => {
          const gap = g.gap || '';
          if (gap.toLowerCase().includes('device compliance')) return 'Enforce device compliance policies';
          if (gap.toLowerCase().includes('remote') && gap.toLowerCase().includes('wipe')) return 'Enable remote device management and wipe capability';
          if (gap.toLowerCase().includes('monitoring')) return 'Strengthen security alert monitoring';
          if (gap.toLowerCase().includes('privilege')) return 'Implement just-in-time privilege elevation';
          return gap.replace(/^Is\s+/i, '').replace(/\?$/, '');
        })
    },
    priority_3: {
      title: 'Evidence and Compliance',
      items: governanceGaps.slice(0, 5).map(g => {
        const gap = g.gap || '';
        if (gap.toLowerCase().includes('evidence')) return 'Prepare structured evidence for audit readiness';
        if (gap.toLowerCase().includes('compliance') && gap.toLowerCase().includes('management')) return 'Establish formal compliance management processes';
        if (gap.toLowerCase().includes('risk register')) return 'Maintain and update the risk register';
        if (gap.toLowerCase().includes('remediation')) return 'Track remediation activities with clear timelines';
        return gap.replace(/^Is\s+/i, '').replace(/\?$/, '');
      })
    }
  };

  // Section 5: Maturity Snapshot
  const strong = domainFindings.filter(d => d.score >= 80).map(d => d.domain_name);
  const moderate = domainFindings.filter(d => d.score >= 60 && d.score < 80).map(d => d.domain_name);
  const weak = domainFindings.filter(d => d.score < 60).map(d => d.domain_name);

  const maturity_snapshot = { strong, moderate, weak };

  return {
    title: 'Executive Brief',
    readiness_summary,
    risk_summary,
    key_gaps,
    action_plan,
    maturity_snapshot
  };
};

/**
 * Map requirement category to clean label for report table
 */
const getRequirementLabel = (category) => {
  const labels = {
    required: 'Required',
    recommended: 'Recommended',
    sufficient: 'No new tool required',
    alternative: 'Alternative',
    not_needed_yet: 'No new tool required'
  };
  return labels[category] || category;
};

/**
 * Generate complete assessment report aligned to canonical schema
 * @param {Object} engagement - Engagement details
 * @param {Object} answers - All questionnaire answers
 * @returns {Object} Complete report matching schema structure
 */
export const generateReport = (engagement, answers, opts = {}) => {
  const timestamp = new Date().toISOString();
  
  // Calculate all scores
  const scoringResults = calculateAssessmentScores(answers);
  const { domainScores, overallScore, overallStatus, gatingStatus } = scoringResults;

  // Get stack recommendations
  const stackRecommendation = getStackRecommendation(answers);

  // Determine classification
  const greenfieldCandidate = isGreenfieldCandidate(domainScores, answers);
  const managedUpliftCandidate = isManagedUpliftCandidate(overallScore, answers);
  const serviceTier = determineServiceTier(overallScore, greenfieldCandidate, managedUpliftCandidate, answers);
  const serviceTierInfo = getServiceTierInfo(serviceTier);
  
  // Determine Environment Type (separate from Service Tier)
  const environmentType = determineEnvironmentType(overallScore, domainScores);
  const environmentTypeInfo = getEnvironmentTypeInfo(environmentType);

  // Generate priority gaps
  const priorityGaps = generatePriorityGaps(answers);

  // Generate domain findings
  const domainFindings = generateDomainFindings(domainScores, answers);

  // Generate roadmap
  const roadmap = generateRoadmap(priorityGaps);

  // Build report data
  const reportData = {
    engagement,
    overallScore,
    overallStatus,
    serviceTier,
    gatingStatus,
    stackRecommendation,
    greenfieldCandidate,
    managedUpliftCandidate,
    priorityGaps,
    domainFindings,
    roadmap
  };

  // Generate executive summary
  const executiveSummary = generateExecutiveSummary(reportData);

  // Derive DISP Submission Readiness
  const dispReadiness = deriveDISPReadiness(scoringResults.enhanced, gatingStatus, answers);

  // Generate ML2 Implementation Blueprint
  const blueprint = generateImplementationBlueprint({
    weightedScore: scoringResults.enhanced?.finalScore ?? overallScore,
    scoreBand: scoringResults.enhanced?.scoreBand || {},
    environmentType,
    evidenceMultiplier: scoringResults.enhanced?.multipliers?.evidence ?? 1,
    policyMultiplier: scoringResults.enhanced?.multipliers?.policyEnforcement ?? 1,
    domainScores,
    priorityGaps,
    stackByLayer: stackRecommendation.byLayer,
    recommendedComponents: stackRecommendation.components
  });

  // Build Stack Analysis (Technology Stack Presence & Maturity layer)
  const stackAnalysis = buildStackAnalysis(answers, priorityGaps);

  // Generate Executive Brief (CEO-friendly summary)
  const executiveBrief = generateExecutiveBrief({
    weightedScore: scoringResults.enhanced?.finalScore ?? overallScore,
    scoreBand: scoringResults.enhanced?.scoreBand || {},
    overallScore,
    overallStatus,
    environmentType,
    environmentTypeInfo,
    serviceTier,
    serviceTierInfo,
    domainFindings,
    priorityGaps,
    gatingStatus,
    dispReadiness,
    stackAnalysis,
    auditFailureLikelihood: scoringResults.enhanced?.auditFailureLikelihood,
    auditFailureMessage: scoringResults.enhanced?.auditFailureMessage
  });

  // Generate DISP Gap Analysis
  const dispGapAnalysis = generateDISPGapAnalysis(answers, gatingStatus.controls, priorityGaps);

  // Generate CSQ Submission Mapping (used by both _report_metadata and hybrid_report)
  const csqMapping = generateCSQMapping(dispGapAnalysis);

  // Build Hybrid DISP Readiness & Assurance Report (additive, modular sections)
  const hybridReport = buildHybridReport({
    engagement,
    answers,
    finalScore: scoringResults.enhanced?.finalScore ?? overallScore,
    scoreBand: scoringResults.enhanced?.scoreBand || {},
    domainScores,
    domainFindings,
    gatingStatus,
    priorityGaps,
    dispReadiness,
    dispGapAnalysis,
    csqMapping,
    evidenceMultiplier: scoringResults.enhanced?.multipliers?.evidence ?? 1,
    policyEnforcementMultiplier: scoringResults.enhanced?.multipliers?.policyEnforcement ?? 1,
  });

  // Build stack_status_rows for report (using new normalized status model)
  const stack_status_rows = stackAnalysis.components.map(c => ({
    layer: c.layer_id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
    component_label: c.label,
    status: c.display_status_label,
    status_color: c.display_status_color,
    requirement: getRequirementLabel(c.requirement_category),
    requirement_text: c.requirement_text,
    action: c.actions[0] || '',
    rationale: c.rationale || ''
  }));

  // Build stack_summary (aligned to new status model)
  const stack_summary = {
    implemented_count: stackAnalysis.summary.implemented,
    needs_improvement_count: stackAnalysis.summary.needs_improvement,
    partial_count: stackAnalysis.summary.partial,
    missing_count: stackAnalysis.summary.missing,
    planned_count: stackAnalysis.summary.planned,
    missing_required_count: stackAnalysis.summary.missing_required_count,
    recommended_new_count: stackAnalysis.summary.recommended_new_count
  };

  // Build blueprint_current_state (4 categories for Phase 6)
  const blueprint_current_state = {
    already_in_place: stackAnalysis.components.filter(c => c.effective_status === 'implemented_effective'),
    uplift_required: stackAnalysis.components.filter(c =>
      c.effective_status === 'implemented_partial' || c.effective_status === 'present_but_ineffective' || c.effective_status === 'implemented_limited'
    ),
    net_new_required: stackAnalysis.components.filter(c =>
      c.inferred_requirement === 'required' && (c.effective_status === 'not_present' || c.effective_status === 'planned_not_implemented' || c.effective_status === 'unknown')
    ),
    optional_enhancements: stackAnalysis.components.filter(c =>
      c.inferred_requirement === 'recommended' && (c.effective_status === 'not_present' || c.effective_status === 'planned_not_implemented')
    )
  };

  // Build schema-compliant output
  return {
    schema_version: '1.0.0',
    generated_at: timestamp,
    
    // Include engagement
    engagement: {
      assessment_id: engagement.assessment_id || `DIQ-${Date.now()}`,
      client_name: engagement.client_name || '',
      assessor_name: engagement.assessor_name || '',
      assessment_date: engagement.assessment_date || timestamp.split('T')[0],
      engagement_type: engagement.engagement_type || 'discovery_only',
      notes: engagement.notes || ''
    },

    // Include all section answers (aligned to schema)
    ...buildSectionAnswers(answers),

    // Derived outputs (computed, not user-entered)
    derived_outputs: {
      overall_ml2_readiness_score: overallScore,
      overall_status: overallStatus,
      recommended_stack_components: stackRecommendation.components.map(c => c.id),
      greenfield_candidate: greenfieldCandidate,
      managed_uplift_candidate: managedUpliftCandidate,
      recommended_service_tier: serviceTier,
      service_tier_info: serviceTierInfo,
      environment_type: environmentType,
      environment_type_info: environmentTypeInfo,
      priority_gaps: priorityGaps.map(gap => ({
        domain: gap.domain,
        gap: gap.gap,
        priority: gap.priority,
        recommended_components: gap.recommended_components
      }))
    },

    // Additional report data (beyond schema)
    _report_metadata: {
      executive_summary: executiveSummary,
      domain_findings: domainFindings,
      stack_by_layer: stackRecommendation.byLayer,
      remediation_roadmap: roadmap,
      gating_control_details: gatingStatus.controls,
      enhanced_scores: scoringResults.enhanced || {},
      disp_readiness: dispReadiness,
      blueprint,
      stack_analysis: stackAnalysis,
      stack_status_rows,
      stack_summary,
      blueprint_current_state,
      executive_brief: executiveBrief,
      disp_gap_analysis: dispGapAnalysis,
      csq_mapping: csqMapping,
      hybrid_report: hybridReport,
      evidence_readiness_assessment: computeEvidenceReadiness(answers),
      branding: opts.branding || null,
      domain_intros: {
        personnel_security: 'Personnel security ensures that individuals with access to Defence information, systems, and facilities are appropriately vetted, trained, and managed throughout their lifecycle. This includes pre-employment screening, security clearances, insider threat awareness, and secure offboarding processes.'
      }
    }
  };
};

// ===== DISP GAP ANALYSIS ENGINE =====

const DISP_DOMAIN_MAP = {
  policy_governance: 'governance',
  compliance_and_governance: 'governance',
  foci: 'governance',
  physical_security: 'physical_security',
  personnel_security: 'personnel_security',
  identity_and_mfa: 'ict_cyber',
  endpoint_management: 'ict_cyber',
  application_control: 'ict_cyber',
  patch_management: 'ict_cyber',
  privileged_access: 'ict_cyber',
  office_macros_and_user_hardening: 'ict_cyber',
  logging_and_monitoring: 'ict_cyber',
  vulnerability_management: 'ict_cyber',
  backups: 'ict_cyber',
};

const DISP_DOMAIN_ORDER = ['governance', 'personnel_security', 'physical_security', 'ict_cyber'];
const DISP_DOMAIN_LABELS = {
  governance: 'Governance',
  personnel_security: 'Personnel Security',
  physical_security: 'Physical Security',
  ict_cyber: 'ICT / Cyber Security',
};

const DISP_REQUIREMENT_TEXT = {};
const DISP_RECOMMENDATION_TEXT = {};
const DISP_EVIDENCE_REQUIRED = {};

// ===== CONTROL GROUPING ENGINE =====

// Maps field_id → group_id for consolidation
const CONTROL_GROUP_MAP = {
  // Governance: Security Policy Framework
  pol_01: 'security_policy_framework', pol_02: 'security_policy_framework',
  pol_03: 'security_policy_framework', pol_04: 'security_policy_framework',
  pol_05: 'security_policy_framework', pol_06: 'security_policy_framework',
  pol_07: 'security_policy_framework',
  disp_10_policy_documentation: 'security_policy_framework',
  disp_11_evidence_readiness: 'security_policy_framework',
  // Governance: Compliance & Risk Management
  frameworks_currently_tracked: 'compliance_risk_mgmt', evidence_collection_method: 'compliance_risk_mgmt',
  compliance_check_frequency: 'compliance_risk_mgmt', risk_register_maintained: 'compliance_risk_mgmt',
  compliance_management_approach: 'compliance_risk_mgmt', disp_09_ict_accreditation: 'compliance_risk_mgmt',
  asr_reporting: 'compliance_risk_mgmt', facility_scope_definition: 'compliance_risk_mgmt',
  // Governance: FOCI
  foci_01_foreign_ownership: 'foci_group', foci_02_foreign_influence: 'foci_group',
  foci_03_foreign_entities_20: 'foci_group', foci_04_foreign_entities_40: 'foci_group',
  foci_05_foreign_control: 'foci_group', foci_06_foreign_arrangements: 'foci_group',
  foci_07_complexity: 'foci_group',
  // ICT: Identity & MFA
  identity_platform: 'identity_mfa', mfa_all_users: 'identity_mfa',
  mfa_privileged_users: 'identity_mfa', mfa_methods: 'identity_mfa',
  conditional_access: 'identity_mfa', shared_or_generic_accounts: 'identity_mfa',
  // ICT: Application Control Framework
  restrict_which_apps_can_run: 'app_control', allow_listing_in_place: 'app_control',
  users_can_install_software: 'app_control', application_execution_monitored: 'app_control',
  // ICT: Patch & Vulnerability Management
  os_patch_management_method: 'patch_vuln', critical_patch_sla: 'patch_vuln',
  central_patch_compliance_tracking: 'patch_vuln', vulnerability_scanning_used: 'patch_vuln',
  real_time_identification_of_unpatched_systems: 'patch_vuln',
  regular_vulnerability_scans: 'patch_vuln', vulnerability_tool: 'patch_vuln',
  vulnerabilities_tracked_and_remediated: 'patch_vuln', remediation_timelines_tracked: 'patch_vuln',
  // ICT: Privileged Access Management
  local_admin_rights_widespread: 'priv_access', separate_admin_accounts: 'priv_access',
  admin_activity_logged_and_reviewed: 'priv_access', temporary_privilege_elevation: 'priv_access',
  // ICT: Security Logging & Monitoring
  centralised_logging: 'logging_monitoring', logging_tool: 'logging_monitoring',
  alerts_actively_monitored: 'logging_monitoring', incident_response_process: 'logging_monitoring',
  // ICT: Endpoint Management
  central_device_management: 'endpoint_mgmt', device_compliance_policies: 'endpoint_mgmt',
  corporate_devices_required: 'endpoint_mgmt', byod_allowed: 'endpoint_mgmt',
  remote_config_or_wipe: 'endpoint_mgmt',
  // ICT: Application Hardening & Macro Security
  internet_macros_blocked: 'macros_hardening', trusted_macro_locations_only: 'macros_hardening',
  browser_security_settings_enforced: 'macros_hardening', legacy_plugins_disabled: 'macros_hardening',
  // ICT: Backup & Recovery
  systems_backed_up: 'backup_recovery', backup_frequency: 'backup_recovery',
  offline_or_immutable_backups: 'backup_recovery', last_successful_restore_test: 'backup_recovery',
  // Physical Security
  disp_06_physical_facility: 'physical_security', disp_07_facility_access_control: 'physical_security',
  disp_13_multiple_locations: 'physical_security',
  // Personnel Security & Clearances
  pre_employment_screening: 'personnel_security_group',
  baseline_vetting_required: 'personnel_security_group',
  security_clearance_management: 'personnel_security_group',
  clearance_tracking: 'personnel_security_group',
  insider_threat_training: 'personnel_security_group',
  offboarding_controls: 'personnel_security_group',
  contractor_security_controls: 'personnel_security_group',
};

// Defines the consolidated narrative for each group
const GROUP_DEFINITIONS = {
  security_policy_framework: {
    name: 'Security Policy Framework',
    requirement: 'DISP requires organisations to establish, maintain, and enforce a comprehensive security policy framework. This includes formal documentation, executive approval, regular review cycles, technical enforcement mechanisms, staff awareness programs, and alignment with Essential Eight ML2 requirements. Policies must be supported by evidence of active governance and compliance.',
    recommendation: 'To meet DISP requirements, the organisation must formalise its security policy suite with documented ownership, approval workflows, and annual review cycles. Policies must be technically enforced where applicable (e.g. via Group Policy or MDM), supported by a staff security awareness program, and aligned to Essential Eight ML2. Evidence of policy distribution, acknowledgement, and compliance should be maintained for audit readiness.',
    evidence: ['Security policy suite with version control', 'Policy approval and ownership records', 'Annual review schedule and completion evidence', 'Staff awareness training records', 'Technical enforcement configuration exports', 'Essential Eight policy alignment mapping'],
  },
  compliance_risk_mgmt: {
    name: 'Compliance & Risk Management Framework',
    requirement: 'DISP requires evidence of structured compliance management including maintained risk registers, defined compliance frameworks, regular assessment cycles, formal evidence collection processes, ICT system accreditation where applicable, a documented process for the preparation and submission of the Annual Security Report (ASR), and a clearly defined and documented DISP registration scope covering all facilities, sites, and environments in scope. The organisation must demonstrate ongoing governance activity rather than point-in-time compliance.',
    recommendation: 'To meet DISP requirements, the organisation must establish a formal compliance management framework with a maintained risk register, defined assessment frequencies, and centralised evidence collection. A formal Annual Security Report (ASR) process must be documented with assigned ownership (e.g. CSO/SO), defined reporting inputs, and an example or draft ASR. The DISP registration scope must be formally defined with all facilities, sites, systems, and classifications documented and mapped. ICT system accreditation should be pursued where required. All compliance activities must be documented and auditable to support CSQ validation.',
    evidence: ['Risk register with treatment plans', 'Compliance framework mapping', 'Evidence collection process documentation', 'Compliance assessment schedule and records', 'ICT accreditation documentation', 'Annual Security Report (ASR) process document with assigned ownership and inputs', 'Example or draft ASR submission', 'Documented DISP registration scope covering facilities, sites, systems, and classifications'],
  },
  foci_group: {
    name: 'Foreign Ownership, Control & Influence (FOCI)',
    requirement: 'DISP requires organisations to identify, assess, and manage risks associated with Foreign Ownership, Control or Influence. This includes maintaining current ownership registers, assessing foreign entity involvement at relevant thresholds, evaluating board-level influence, and documenting mitigation strategies to ensure Defence-related activities are not subject to external influence.',
    recommendation: 'To meet DISP requirements, the organisation must maintain a current ownership register, conduct formal FOCI risk assessments covering all relevant thresholds (20% and 40% foreign entity involvement), document board structures and key personnel foreign connections, and develop mitigation strategies where risks are identified. All assessments must be periodically reviewed.',
    evidence: ['Ownership register', 'Board structure and key personnel documentation', 'FOCI risk assessment records', 'Foreign entity threshold analysis', 'Mitigation strategy documentation'],
  },
  identity_mfa: {
    name: 'Identity & Multi-Factor Authentication',
    requirement: 'DISP and Essential Eight Maturity Level 2 require that multi-factor authentication is enforced across all users, with particular emphasis on privileged accounts. The organisation must implement a centralised identity platform with conditional access policies, phishing-resistant MFA methods, and controls to prevent the use of shared or generic accounts.',
    recommendation: 'To meet DISP requirements, the organisation must enforce MFA across all privileged and standard accounts using a centralised identity platform (e.g. Entra ID) with conditional access policies. MFA methods should be phishing-resistant where possible. Shared and generic accounts must be eliminated or brought under strict governance with audit logging. Enforcement must be validated through authentication logs.',
    evidence: ['MFA policy document', 'Identity platform configuration', 'Conditional access policy exports', 'Authentication logs demonstrating enforcement', 'User account audit showing MFA coverage', 'Shared account governance records'],
  },
  app_control: {
    name: 'Application Control Framework',
    requirement: 'DISP requires the implementation of a comprehensive application control framework to ensure only approved and trusted software can execute within the environment. This includes allow-listing, restriction of user-initiated software installation, and monitoring of application execution to detect and prevent malicious code.',
    recommendation: 'To meet DISP requirements, application control must be implemented using allow-listing technology to restrict execution to approved software. Users must not be able to install unapproved applications. Application execution should be centrally monitored and logged, with policies managed through a centralised platform and regularly tested for bypass resistance.',
    evidence: ['Application control policy', 'Allow-list configuration and rules', 'Evidence of unapproved software blocking', 'Application execution monitoring logs', 'Change management records for approved applications'],
  },
  patch_vuln: {
    name: 'Patch & Vulnerability Management',
    requirement: 'DISP and Essential Eight ML2 require organisations to apply security patches within defined timeframes, maintain real-time visibility of vulnerable and unpatched systems, conduct regular vulnerability assessments, and track remediation through to completion. The patching process must be formalised with SLAs for critical, high, and routine updates.',
    recommendation: 'To meet DISP requirements, the organisation must implement a formal patch management process with defined SLAs for critical and routine updates, supported by automated vulnerability scanning across all in-scope systems. Unpatched systems must be identifiable in near real-time. Vulnerabilities must be tracked through a centralised register with remediation timelines enforced and exceptions formally documented.',
    evidence: ['Patch management policy with SLAs', 'Vulnerability scan reports with coverage metrics', 'Patch compliance dashboard or report', 'Remediation tracking register', 'Evidence of critical patch deployment within SLA', 'Risk acceptance documentation for exceptions'],
  },
  priv_access: {
    name: 'Privileged Access Management',
    requirement: 'DISP requires strict control and monitoring of privileged accounts, including segregation from standard user accounts, enforcement of least privilege principles, logging and regular review of administrative activity, and just-in-time privilege elevation where operationally feasible.',
    recommendation: 'To meet DISP requirements, privileged accounts must be separated from standard user accounts and managed through a PAM solution or equivalent governance model. Local admin rights must be removed from standard users. All administrative activity must be logged and subject to regular review. Just-in-time or time-limited privilege elevation should be implemented to reduce standing access risk.',
    evidence: ['Privileged account register', 'PAM solution configuration and logs', 'Access review records and schedule', 'Evidence of admin/user account segregation', 'Just-in-time elevation configuration'],
  },
  logging_monitoring: {
    name: 'Security Logging & Monitoring',
    requirement: 'DISP requires centralised logging, monitoring, and analysis of security events to enable detection, investigation, and response to malicious activity. This includes implementation of a SIEM or equivalent platform, active alert monitoring, and a defined incident response process.',
    recommendation: 'To meet DISP requirements, the organisation must implement a SIEM solution (e.g. Microsoft Sentinel) to centralise log collection across all in-scope systems. Alerts must be actively monitored with defined escalation procedures. A formal incident response process must be documented, tested, and supported by evidence of operational effectiveness.',
    evidence: ['SIEM architecture diagram', 'Log ingestion coverage reports', 'Alert rules and escalation procedures', 'Incident response plan and test records', 'Log retention policy documentation'],
  },
  endpoint_mgmt: {
    name: 'Endpoint Management',
    requirement: 'DISP requires endpoint devices to be centrally managed, secured, and monitored. This includes device compliance policies, controls over corporate and BYOD devices, encryption enforcement, and the ability to remotely configure or wipe devices to prevent data loss in the event of compromise or theft.',
    recommendation: 'To meet DISP requirements, all endpoint devices must be enrolled in a centralised management platform (e.g. Intune) with enforced security baselines and compliance policies. BYOD must be governed or restricted. Device encryption must be mandatory. Remote wipe and configuration capability must be in place and tested for all managed devices.',
    evidence: ['Device management platform configuration', 'Security baseline compliance reports', 'Encryption status reports', 'BYOD policy and governance records', 'Remote wipe capability evidence and test records'],
  },
  macros_hardening: {
    name: 'Application Hardening & Macro Security',
    requirement: 'DISP requires controls to restrict the execution of high-risk features such as macros and to harden user applications against common exploitation techniques. This includes blocking internet-sourced macros, restricting macro execution to trusted locations, enforcing browser security settings, and disabling legacy plugins and features.',
    recommendation: 'To meet DISP requirements, macro execution must be restricted to signed or trusted locations via Group Policy, with internet-sourced macros blocked by default. Browser security settings must be centrally enforced, legacy plugins disabled, and user-facing applications hardened against common exploitation vectors. All hardening configurations must be documented and auditable.',
    evidence: ['Group Policy settings for macro restrictions', 'Browser security configuration exports', 'Legacy plugin restriction evidence', 'Application hardening baseline documentation', 'Testing evidence of macro and exploitation blocking'],
  },
  backup_recovery: {
    name: 'Backup & Recovery',
    requirement: 'DISP requires organisations to maintain regular, tested, and immutable backups to ensure recovery capability in the event of data loss, ransomware, or system failure. Backups must cover all critical systems, follow defined schedules, include offline or immutable copies, and be subject to regular restore testing.',
    recommendation: 'To meet DISP requirements, backups must follow the 3-2-1 principle covering all critical systems with defined frequency. Offline or immutable copies must be maintained to protect against ransomware. Restore testing must be conducted regularly with documented results. Backup coverage, retention, and recovery objectives must be formally defined and reviewed.',
    evidence: ['Backup policy with coverage and retention definitions', 'Backup schedule and completion evidence', 'Immutability configuration documentation', 'Restore test results with dates', 'Recovery objective definitions (RPO/RTO)'],
  },
  physical_security: {
    name: 'Physical Security & Facilities',
    requirement: 'DISP requires physical security controls including access restrictions, visitor management, and facility protections proportionate to the classification of information handled. Organisations operating across multiple locations must ensure consistent physical security standards are applied at all sites.',
    recommendation: 'To meet DISP requirements, physical access controls must be implemented at all facilities handling Defence information, including electronic access systems, visitor management procedures, and security monitoring. Multi-site organisations must ensure consistent standards across all locations. Physical security assessments should be conducted and documented regularly.',
    evidence: ['Physical access control logs and configuration', 'Visitor management procedures and records', 'Facility security assessment reports', 'Multi-site security standards documentation', 'CCTV and alarm system documentation'],
  },
  personnel_security_group: {
    name: 'Personnel Security & Clearances',
    requirement: 'DISP requires organisations to vet, train, and manage personnel with access to Defence information, systems, and facilities throughout their lifecycle. This includes pre-employment screening aligned to recognised standards (e.g. AS4811), baseline vetting for all personnel accessing Defence-related environments, the capability to sponsor and maintain security clearances (Baseline, NV1, NV2, PV), active tracking of clearance validity, insider threat awareness training, secure offboarding procedures, and equivalent personnel security controls applied to contractors and third parties.',
    recommendation: 'To meet DISP requirements, the organisation must formalise pre-employment screening aligned to AS4811, ensure all personnel (including contractors) accessing Defence-related systems are subject to appropriate baseline vetting, and establish a structured clearance sponsorship and management capability. Clearances must be centrally tracked with proactive renewal monitoring. A formal insider threat and security awareness training program must be in place with participation tracking. Offboarding procedures must guarantee timely revocation of access and asset return. Equivalent personnel security controls must be enforced for contractors, subcontractors, and third parties.',
    evidence: [
      'Pre-employment screening policy aligned to AS4811',
      'Records of identity verification, background checks, and employment history validation',
      'Baseline vetting register covering employees and contractors',
      'Security clearance sponsorship and management procedures',
      'Clearance register with status, level, and expiry tracking',
      'Insider threat and security awareness training program with participation records',
      'Offboarding checklist and access revocation evidence',
      'Contractor and third-party personnel security agreements'
    ],
  },
};

// Domain insight sentences - concise consultant observations per group
const DOMAIN_INSIGHTS = {
  security_policy_framework: 'This reflects an absence of formalised governance rather than a structured policy management lifecycle.',
  compliance_risk_mgmt: 'This reflects a lack of integrated governance and compliance oversight across the organisation.',
  foci_group: 'This reflects insufficient attention to foreign influence risk, which Defence treats as a threshold issue during DISP assessment.',
  identity_mfa: 'This reflects a fragmented identity and access management model rather than a centrally enforced control framework.',
  app_control: 'This reflects an environment where software execution is not governed, leaving the organisation exposed to unapproved and potentially malicious code.',
  patch_vuln: 'This reflects an immature vulnerability lifecycle where patching and remediation are reactive rather than governed by policy.',
  priv_access: 'This reflects insufficient separation and oversight of administrative privilege, a key area of Defence scrutiny.',
  logging_monitoring: 'This reflects an immature security monitoring capability with limited central visibility into threats and incidents.',
  endpoint_mgmt: 'This reflects a decentralised device management approach that cannot consistently enforce security baselines.',
  macros_hardening: 'This reflects insufficient hardening of the user application layer, a primary exploitation vector addressed by Essential Eight.',
  backup_recovery: 'This reflects a backup posture that may not withstand a ransomware event or support timely recovery of Defence-related systems.',
  physical_security: 'This reflects a physical security posture that has not been formally assessed against DISP facility requirements.',
  personnel_security_group: 'This reflects an immature personnel security posture where workforce trust controls, clearance management, and insider threat mitigation are not consistently governed across the personnel lifecycle.',
};

// Pluralisation helpers
const ctrlWord = (n) => n === 1 ? 'control' : 'controls';
const isAre = (n) => n === 1 ? 'is' : 'are';
const hasHave = (n) => n === 1 ? 'has' : 'have';

/**
 * Generate a varied, business-impact finding based on the aggregate state of controls in a group.
 * Uses 4 structural patterns rotated across groups.
 */
const generateGroupFinding = (groupId, groupName, controls, variationIndex) => {
  const failCount = controls.filter(c => c.score === 0).length;
  const partialCount = controls.filter(c => c.score === 1).length;
  const total = failCount + partialCount;
  const hasGating = controls.some(c => c.is_gating && c.score < 2);
  const insight = DOMAIN_INSIGHTS[groupId] || '';
  const nameLower = groupName.toLowerCase();

  // Select structural pattern (A=State>Risk>Impact, B=Risk-first, C=Impact-first, D=Root-cause)
  const pattern = variationIndex % 4;
  let finding;

  if (failCount > 0 && partialCount === 0) {
    // ALL FAIL scenarios
    switch (pattern) {
      case 0: // Pattern A: State > Risk > Impact
        finding = `${groupName} ${ctrlWord(failCount)} ${isAre(failCount)} not currently implemented. The organisation cannot demonstrate compliance with DISP requirements in this area, which increases the likelihood of CSQ rejection or Defence requesting a formal remediation plan before reassessment.`;
        break;
      case 1: // Pattern B: Risk-first
        finding = `There is a significant risk that ${nameLower} compliance cannot be demonstrated under audit conditions. ${failCount} ${ctrlWord(failCount)} ${isAre(failCount)} absent from the current environment, and Defence assessors will expect documented evidence of implementation and operational effectiveness in this domain.`;
        break;
      case 2: // Pattern C: Impact-first
        finding = `This gap will likely delay DISP submission readiness. ${groupName} ${ctrlWord(failCount)} ${isAre(failCount)} not in place, creating a material compliance deficit that will likely result in Defence requesting clarification or additional evidence before progressing the application.`;
        break;
      default: // Pattern D: Root cause
        finding = `The primary issue in this domain is the absence of ${nameLower} capability. Without these ${ctrlWord(failCount)} in place, the organisation lacks the foundational controls Defence expects to see during CSQ validation, reducing confidence in the broader control environment.`;
        break;
    }
  } else if (failCount === 0 && partialCount > 0) {
    // ALL PARTIAL scenarios
    switch (pattern) {
      case 0:
        finding = `${groupName} ${ctrlWord(partialCount)} ${isAre(partialCount)} partially implemented, however enforcement and coverage are inconsistent across the environment. This limits the organisation's ability to demonstrate repeatable control operation and will likely result in Defence requesting additional evidence of consistent application.`;
        break;
      case 1:
        finding = `There is a risk that ${nameLower} controls will not satisfy Defence assessment criteria. While ${partialCount} ${ctrlWord(partialCount)} ${hasHave(partialCount)} some level of implementation, the lack of uniform enforcement means compliance cannot be reliably demonstrated during CSQ validation.`;
        break;
      case 2:
        finding = `Partial implementation of ${nameLower} controls creates risk of audit failure during DISP assessment. ${partialCount} ${ctrlWord(partialCount)} ${isAre(partialCount)} in place but applied inconsistently, which Defence assessors are likely to flag as requiring structured remediation before submission.`;
        break;
      default:
        finding = `The underlying issue is that ${nameLower} processes exist but are not uniformly applied or centrally governed. This fragmented approach means the organisation cannot consistently demonstrate control effectiveness, which may delay Defence contract eligibility.`;
        break;
    }
  } else {
    // MIXED scenarios
    switch (pattern) {
      case 0:
        finding = `Of the ${total} assessed ${ctrlWord(total)} in this group, ${failCount} ${isAre(failCount)} not implemented and ${partialCount} ${isAre(partialCount)} only partially in place. This combination of gaps creates significant risk to DISP compliance and is likely to result in Defence requesting a structured remediation plan.`;
        break;
      case 1:
        finding = `There is a material risk that ${nameLower} requirements cannot be met in the current state. ${failCount} ${ctrlWord(failCount)} ${isAre(failCount)} absent and ${partialCount} ${hasHave(partialCount)} inconsistent coverage, which collectively undermines the organisation's ability to pass CSQ validation in this domain.`;
        break;
      case 2:
        finding = `These gaps may delay DISP submission readiness. The ${nameLower} domain shows ${failCount} unimplemented ${ctrlWord(failCount)} alongside ${partialCount} with incomplete enforcement, creating a compliance profile that is unlikely to satisfy Defence assessment criteria without targeted uplift.`;
        break;
      default:
        finding = `The root cause is an absence of centralised ${nameLower} governance. With ${failCount} ${ctrlWord(failCount)} missing and ${partialCount} inconsistently applied, the organisation's control maturity in this area falls below the threshold Defence expects for DISP membership.`;
        break;
    }
  }

  // Add gating warning if applicable
  if (hasGating) {
    finding += ' This group includes critical gating controls and failure to address these issues will prevent successful DISP submission.';
  }

  // Add domain insight sentence
  if (insight) {
    finding += ` ${insight}`;
  }

  return finding;
};

/**
 * Generate DISP Gap Analysis with smart control grouping and deduplication.
 * Groups related controls into consolidated narratives per DISP domain.
 */
export const generateDISPGapAnalysis = (answers, gatingControls, priorityGaps) => {
  const gatingFieldIds = new Set(gatingControls.map(c => c.field_id));
  const priorityByField = {};
  priorityGaps.forEach(g => { priorityByField[g.field_id] = g; });

  // Step 1: Collect all raw controls that meet the filter criteria
  const rawControls = [];

  SCORED_SECTIONS.forEach(sectionId => {
    const dispDomain = DISP_DOMAIN_MAP[sectionId];
    if (!dispDomain) return;
    const section = QUESTION_CONFIG[sectionId];
    if (!section) return;

    section.questions.forEach(q => {
      if (q.weight === 0) return;
      const answer = answers[q.field_id];
      const score = getAnswerScore(answer, q);
      const isGating = gatingFieldIds.has(q.field_id) || q.gating_rule === true;
      const gap = priorityByField[q.field_id];
      const priority = gap?.priority || (q.criticality === 'critical' ? 'critical' : q.criticality === 'high' ? 'high' : 'medium');

      const isFail = score === 0;
      const isPartial = score === 1;
      const isCritHigh = priority === 'critical' || priority === 'high';
      if (!isFail && !isPartial && !isCritHigh && !isGating) return;

      rawControls.push({
        field_id: q.field_id,
        section_id: sectionId,
        disp_domain: dispDomain,
        score,
        priority,
        is_gating: isGating,
      });
    });
  });

  // Step 2: Group controls by group_id within each domain
  const groupBuckets = {};
  rawControls.forEach(ctrl => {
    const groupId = CONTROL_GROUP_MAP[ctrl.field_id];
    if (!groupId) return;
    if (!groupBuckets[groupId]) {
      groupBuckets[groupId] = { domain: ctrl.disp_domain, controls: [] };
    }
    groupBuckets[groupId].controls.push(ctrl);
  });

  // Step 3: Build consolidated control objects per group
  const domainBuckets = {};
  let variationCounter = 0;

  Object.entries(groupBuckets).forEach(([groupId, bucket]) => {
    const def = GROUP_DEFINITIONS[groupId];
    if (!def || bucket.controls.length === 0) return;

    const domain = bucket.domain;
    const controls = bucket.controls;
    const failCount = controls.filter(c => c.score === 0).length;
    const partialCount = controls.filter(c => c.score === 1).length;
    const hasGating = controls.some(c => c.is_gating && c.score < 2);
    const hasCritical = controls.some(c => c.priority === 'critical');
    const hasHigh = controls.some(c => c.priority === 'high');

    // Worst compliance status in the group
    let complianceStatus = 'compliant';
    if (failCount > 0) complianceStatus = 'non_compliant';
    else if (partialCount > 0) complianceStatus = 'partially_compliant';

    // Highest priority in the group
    let groupPriority = 'medium';
    if (hasCritical) groupPriority = 'critical';
    else if (hasHigh) groupPriority = 'high';

    const groupName = (hasCritical ? 'CRITICAL: ' : '') + def.name;

    const finding = generateGroupFinding(groupId, def.name, controls, variationCounter);
    variationCounter++;

    const consolidated = {
      short_name: groupName,
      group_id: groupId,
      control_count: controls.length,
      fail_count: failCount,
      partial_count: partialCount,
      score: failCount > 0 ? 0 : partialCount > 0 ? 1 : 2,
      priority: groupPriority,
      is_gating: hasGating,
      compliance_status: complianceStatus,
      requirement: def.requirement,
      finding,
      recommendation: def.recommendation,
      evidence_required: def.evidence,
    };

    if (!domainBuckets[domain]) {
      domainBuckets[domain] = { label: DISP_DOMAIN_LABELS[domain], controls: [] };
    }
    domainBuckets[domain].controls.push(consolidated);
  });

  // Step 4: Sort by priority within each domain and return
  const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  return DISP_DOMAIN_ORDER
    .filter(d => domainBuckets[d])
    .map(d => {
      const bucket = domainBuckets[d];
      bucket.controls.sort((a, b) => {
        const pd = (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3);
        if (pd !== 0) return pd;
        return a.score - b.score;
      });
      return { domain_id: d, label: bucket.label, controls: bucket.controls };
    });
};

// ===== CSQ SUBMISSION MAPPING ENGINE =====

const CSQ_DOMAIN_MAP = {
  governance: 'Governance & Risk Management',
  personnel_security: 'Personnel Security',
  physical_security: 'Physical Security',
  ict_cyber: 'ICT Security',
};

const CSQ_DOMAIN_ORDER = ['Governance & Risk Management', 'Personnel Security', 'Physical Security', 'ICT Security'];

const CSQ_CATEGORY_MAP = {
  security_policy_framework: 'Governance',
  compliance_risk_mgmt: 'Governance',
  foci_group: 'Governance',
  identity_mfa: 'Access Control',
  priv_access: 'Access Control',
  app_control: 'Application Security',
  macros_hardening: 'Application Security',
  patch_vuln: 'System Maintenance',
  logging_monitoring: 'Monitoring & Incident Detection',
  endpoint_mgmt: 'Device Security',
  backup_recovery: 'Data Protection',
  physical_security: 'Physical Security',
  personnel_security_group: 'Personnel Security',
};

const CSQ_GUIDANCE = {
  non_compliant: [
    'The organisation is currently implementing controls to meet this requirement. While initial planning has commenced, full enforcement and coverage across all in-scope systems has not yet been achieved. A structured uplift program is underway to address these gaps prior to submission.',
    'This control area is under active development. The organisation recognises the requirement and has initiated a remediation program; however, implementation is not yet at a level that can be evidenced for CSQ purposes.',
    'Controls in this area are not yet operational. The organisation has identified the gap and is working toward implementation as part of a broader DISP readiness program.',
  ],
  partially_compliant: [
    'The organisation has implemented controls in this area; however, coverage is not yet consistent across all systems and environments. Work is underway to standardise enforcement and ensure full alignment with DISP requirements.',
    'Controls are in place for this requirement, though enforcement varies across the environment. The organisation is undertaking structured remediation to achieve consistent coverage and generate audit-ready evidence.',
    'Implementation exists but is not uniformly applied. The organisation is progressing toward full enforcement and is developing documentation to support CSQ validation.',
  ],
  compliant: [
    'The organisation has implemented and operationalised controls in this area. These controls are consistently enforced across all in-scope systems and supported by audit evidence demonstrating ongoing effectiveness.',
  ],
};

const CSQ_REVIEW_RISK = {
  non_compliant: 'High risk of CSQ rejection if submitted in current state.',
  partially_compliant: 'Likely to trigger Defence clarification requests or require additional evidence.',
  compliant: 'Low risk, provided evidence is complete and current.',
};

const CSQ_POSITIONING_TIP = {
  non_compliant: 'Recommend deferring submission until control enforcement is demonstrable and initial evidence is available.',
  partially_compliant: 'Submission possible with clear explanation of uplift roadmap and timeline for full enforcement.',
  compliant: 'Suitable for submission with supporting evidence attached.',
};

// Category-specific CSQ response text (control-aware, not templated)
const CSQ_RESPONSE_SPECIFIC = {
  'Access Control': {
    non_compliant: 'Multi-factor authentication (MFA) is currently being implemented across privileged accounts using a centralised identity platform (e.g. Microsoft Entra ID); however, enforcement is not yet consistent across all in-scope systems. Privileged access controls are partially in place, but full segregation of administrative accounts, audit visibility, and conditional access enforcement have not yet been achieved.',
    partially_compliant: 'The organisation has implemented MFA and identity controls across parts of the environment using a centralised identity platform. Privileged account segregation is in progress. Conditional access policies are partially deployed; however, consistent enforcement and comprehensive audit logging across all user accounts are still being standardised.',
    compliant: 'The organisation enforces multi-factor authentication across all privileged and standard accounts via a centralised identity platform with conditional access policies. Privileged accounts are segregated, monitored, and subject to regular access reviews. Authentication logs demonstrate consistent enforcement.',
  },
  'Application Security': {
    non_compliant: 'Application control and hardening measures are not yet fully implemented. While initial assessment of allow-listing requirements has commenced, enforcement of application execution restrictions, macro controls, and browser hardening across all endpoints has not been achieved.',
    partially_compliant: 'The organisation has deployed application control measures on a subset of systems; however, allow-listing enforcement, macro restriction, and application hardening are not yet consistently applied across all in-scope endpoints. Work is underway to standardise these controls.',
    compliant: 'Application control is enforced via allow-listing technology across all in-scope systems. Macro execution is restricted to trusted locations, browser security settings are centrally enforced, and legacy plugins are disabled. Controls are supported by documented baselines and testing evidence.',
  },
  'System Maintenance': {
    non_compliant: 'A formal patch management process is under development; however, defined SLAs for critical and routine updates, real-time visibility of unpatched systems, and centralised vulnerability tracking are not yet consistently implemented across the environment.',
    partially_compliant: 'The organisation has established a patch management process with some defined SLAs. Vulnerability scanning is deployed across critical systems; however, coverage is not yet comprehensive, and remediation tracking requires further formalisation to demonstrate consistent compliance.',
    compliant: 'The organisation operates a formal patch management process with defined SLAs for critical, high, and routine updates. Vulnerability scanning covers all in-scope systems, and remediation is tracked through a centralised register with evidence of SLA adherence.',
  },
  'Monitoring & Incident Detection': {
    non_compliant: 'Centralised logging and security monitoring capabilities are not yet operational. While the requirement for a SIEM solution has been identified, log collection, alert monitoring, and incident response processes are not yet implemented at a level that can be evidenced for CSQ purposes.',
    partially_compliant: 'The organisation has deployed a SIEM solution (e.g. Microsoft Sentinel) with partial log ingestion coverage. Alert rules and monitoring procedures are in place for critical systems; however, coverage across all in-scope systems and formal incident response testing require further development.',
    compliant: 'The organisation operates a centralised SIEM solution with comprehensive log ingestion across all in-scope systems. Alert rules are actively monitored with defined escalation procedures, and the incident response process is documented and regularly tested.',
  },
  'Device Security': {
    non_compliant: 'Endpoint devices are not yet fully enrolled in a centralised management platform. While initial deployment of device compliance controls has commenced, consistent enforcement of security baselines, encryption, and remote management capability across all devices has not been achieved.',
    partially_compliant: 'The organisation has enrolled a proportion of devices in a centralised management platform (e.g. Microsoft Intune). Security baselines and compliance policies are partially enforced; however, coverage across all endpoints, encryption validation, and remote wipe capability require further rollout.',
    compliant: 'All endpoint devices are enrolled in a centralised management platform with enforced security baselines, encryption, and compliance policies. Remote wipe capability is operational and tested. Device compliance reports demonstrate consistent enforcement.',
  },
  'Data Protection': {
    non_compliant: 'Backup processes are not yet implemented to the standard required for DISP compliance. While some backups exist, defined schedules, immutable copies, and regular recovery testing are not yet in place across all critical systems.',
    partially_compliant: 'The organisation performs regular backups of critical systems; however, immutable or offline copies are not yet fully configured, and recovery testing has not been conducted recently. Work is underway to formalise backup policies and establish regular restore testing.',
    compliant: 'The organisation maintains regular, tested, and immutable backups across all critical systems. Backup schedules, retention policies, and recovery objectives are formally defined. Restore tests are conducted regularly with documented results.',
  },
  'Governance': {
    non_compliant: 'Formal security policies, compliance frameworks, and risk management processes are under development. While the organisation has identified DISP governance requirements, documented policies, formal risk registers, Annual Security Report (ASR) processes, a defined DISP facility and registration scope, and evidence collection processes are not yet operational.',
    partially_compliant: 'The organisation has established security policies and a compliance framework; however, policy enforcement, risk register maintenance, Annual Security Report (ASR) preparation, DISP facility scope documentation, and evidence collection are not yet consistently applied. FOCI assessments and ICT accreditation activities are in progress.',
    compliant: 'The organisation maintains a comprehensive security policy framework with formal risk management, compliance tracking, and evidence collection processes. Policies are reviewed annually, FOCI assessments are current, the DISP registration scope (facilities, sites, and environments) is formally documented, and a structured Annual Security Report (ASR) process with assigned ownership is in place. All governance activities are documented and auditable.',
  },
  'Physical Security': {
    non_compliant: 'Physical security controls are not yet formally assessed against DISP facility requirements. While basic access controls exist, visitor management, facility security assessments, and multi-site security standards have not been documented to the level required for CSQ validation.',
    partially_compliant: 'Physical access controls and visitor management procedures are in place at the primary facility; however, formal security assessments and consistent standards across all operational locations have not yet been fully documented.',
    compliant: 'Physical security controls are in place across all operational facilities, including electronic access controls, visitor management procedures, and documented security assessments. Multi-site standards are consistently applied.',
  },
  'Personnel Security': {
    non_compliant: 'Personnel security controls are not yet operating at the level required for DISP compliance. Pre-employment screening aligned to recognised standards (e.g. AS4811), baseline vetting of personnel accessing Defence-related environments, formal clearance sponsorship and tracking, insider threat awareness training, and structured offboarding procedures are not yet consistently in place. Equivalent controls for contractors and third parties are not enforced.',
    partially_compliant: 'The organisation has implemented elements of personnel security including some pre-employment screening and offboarding activity; however, alignment to AS4811, baseline vetting of all personnel with access to Defence-related systems, formal clearance management and tracking, insider threat training, and consistent application of equivalent controls to contractors are not yet uniformly enforced or evidenced.',
    compliant: 'The organisation operates a formal personnel security framework. Pre-employment screening is aligned to AS4811, baseline vetting is enforced for all personnel (including contractors) accessing Defence-related systems, security clearances are sponsored, managed, and centrally tracked through to renewal, insider threat and security awareness training is conducted with participation records, offboarding is formally controlled, and equivalent controls are enforced consistently for third parties.',
  },
};

// Category-specific minimum viable submission path (2-4 actions)
const CSQ_MINIMUM_PATH = {
  'Access Control': [
    'Enforce MFA across all privileged accounts via centralised identity platform',
    'Remove or govern all shared and generic accounts',
    'Produce authentication logs demonstrating consistent MFA enforcement',
    'Document privileged account register with access review schedule',
  ],
  'Application Security': [
    'Deploy application allow-listing on critical systems with enforcement evidence',
    'Restrict macro execution to trusted locations via Group Policy',
    'Document application hardening baselines and browser security settings',
  ],
  'System Maintenance': [
    'Define and document patch SLAs for critical, high, and routine updates',
    'Deploy vulnerability scanning across all in-scope systems',
    'Generate initial patch compliance report demonstrating SLA adherence',
  ],
  'Monitoring & Incident Detection': [
    'Implement centralised log collection via SIEM across critical systems',
    'Configure alert rules for priority security events',
    'Document and test incident response process with evidence of execution',
  ],
  'Device Security': [
    'Enrol all corporate endpoints in centralised management platform',
    'Enforce security baselines and encryption across managed devices',
    'Validate and document remote wipe capability',
  ],
  'Data Protection': [
    'Formalise backup policy with coverage, retention, and recovery objectives',
    'Configure immutable or offline backup copies for critical systems',
    'Conduct and document backup restore test',
  ],
  'Governance': [
    'Formalise and approve security policy suite with review schedule',
    'Establish risk register with documented treatment plans',
    'Complete FOCI assessment and document ownership structures',
    'Define DISP facility scope and included locations',
    'Establish ASR reporting framework and ownership',
  ],
  'Physical Security': [
    'Conduct formal facility security assessment against DISP requirements',
    'Document visitor management procedures and access control logs',
    'Establish consistent physical security standards across all locations',
  ],
  'Personnel Security': [
    'Formalise pre-employment screening process aligned to AS4811 and document records of identity, background, and employment history checks',
    'Establish a baseline vetting register covering all employees and contractors with access to Defence-related systems, information, or facilities',
    'Implement clearance sponsorship and central tracking with proactive renewal monitoring prior to expiry',
    'Deploy a formal insider threat and security awareness training program with participation tracking, supported by structured offboarding procedures',
  ],
};

// Enhanced supporting evidence with Defence context
const CSQ_EVIDENCE_WITH_CONTEXT = {
  'Access Control': [
    { item: 'MFA policy document', context: 'Used by Defence to validate MFA governance and enforcement scope' },
    { item: 'Conditional access configuration export', context: 'Demonstrates technical enforcement of access policies' },
    { item: 'Authentication logs demonstrating enforcement', context: 'Used by Defence to validate control effectiveness across all users' },
    { item: 'Privileged account register', context: 'Demonstrates segregation and governance of administrative accounts' },
    { item: 'Access review records', context: 'Evidence of ongoing oversight and least privilege enforcement' },
  ],
  'Application Security': [
    { item: 'Application control policy and allow-list rules', context: 'Demonstrates governance and approved software inventory' },
    { item: 'Macro restriction Group Policy settings', context: 'Validates enforcement of macro controls across endpoints' },
    { item: 'Testing evidence of unapproved software blocking', context: 'Proves control effectiveness under real conditions' },
    { item: 'Application hardening baseline documentation', context: 'Demonstrates consistent hardening across user-facing applications' },
  ],
  'System Maintenance': [
    { item: 'Patch management policy with SLAs', context: 'Defines expected patching timeframes for Defence assessment' },
    { item: 'Vulnerability scan reports with coverage metrics', context: 'Used to demonstrate adherence to defined patching SLAs' },
    { item: 'Patch compliance dashboard or report', context: 'Provides evidence of current patch posture across in-scope systems' },
    { item: 'Remediation tracking register', context: 'Demonstrates structured approach to vulnerability resolution' },
  ],
  'Monitoring & Incident Detection': [
    { item: 'SIEM architecture diagram', context: 'Demonstrates centralised monitoring design and coverage' },
    { item: 'Log ingestion coverage reports', context: 'Validates that all in-scope systems are feeding into SIEM' },
    { item: 'Alert rules and escalation procedures', context: 'Used to validate monitoring and incident detection capability' },
    { item: 'Incident response plan and test records', context: 'Demonstrates organisational readiness to respond to security events' },
  ],
  'Device Security': [
    { item: 'Device management platform configuration', context: 'Demonstrates centralised device governance and control' },
    { item: 'Security baseline compliance reports', context: 'Validates consistent enforcement across all managed endpoints' },
    { item: 'Encryption status reports', context: 'Proves data-at-rest protection on all devices' },
    { item: 'Remote wipe capability test evidence', context: 'Demonstrates ability to protect data on lost or compromised devices' },
  ],
  'Data Protection': [
    { item: 'Backup policy with coverage and retention definitions', context: 'Establishes governance framework for data protection' },
    { item: 'Backup schedule and completion evidence', context: 'Validates regular execution of backup processes' },
    { item: 'Immutability configuration documentation', context: 'Demonstrates protection against ransomware and tampering' },
    { item: 'Restore test results with dates', context: 'Proves recovery capability under real-world conditions' },
  ],
  'Governance': [
    { item: 'Security policy suite with version control', context: 'Demonstrates formal governance and active policy management' },
    { item: 'Risk register with treatment plans', context: 'Used by Defence to assess risk management maturity' },
    { item: 'Compliance framework mapping', context: 'Shows alignment between organisational controls and DISP requirements' },
    { item: 'FOCI risk assessment records', context: 'Validates assessment of foreign ownership and influence risks' },
    { item: 'Annual review schedule and completion evidence', context: 'Demonstrates ongoing governance activity' },
    { item: 'Annual Security Report (ASR) process document with assigned ownership', context: 'Demonstrates ongoing DISP reporting obligation is formally governed' },
    { item: 'Example or draft Annual Security Report (ASR)', context: 'Evidence that ASR reporting inputs and structure are defined' },
    { item: 'Documented DISP registration scope (facilities, sites, systems, classifications)', context: 'Used by Defence to confirm which facilities and environments are in-scope for DISP' },
  ],
  'Physical Security': [
    { item: 'Physical access control logs and configuration', context: 'Demonstrates who can access Defence-relevant areas' },
    { item: 'Visitor management procedures and records', context: 'Validates controls over non-cleared personnel access' },
    { item: 'Facility security assessment reports', context: 'Demonstrates formal evaluation against DISP physical requirements' },
  ],
  'Personnel Security': [
    { item: 'Pre-employment screening policy aligned to AS4811', context: 'Demonstrates formal alignment to recognised personnel screening standards' },
    { item: 'Identity verification, background check, and employment history validation records', context: 'Validates that screening is consistently applied to all personnel' },
    { item: 'Baseline vetting register for employees and contractors', context: 'Used by Defence to validate that all personnel with access are appropriately vetted' },
    { item: 'Security clearance register with status, level, and expiry tracking', context: 'Demonstrates active management and timely renewal of personnel clearances' },
    { item: 'Insider threat and security awareness training records', context: 'Provides evidence of workforce awareness of suspicious or malicious behaviour reporting' },
    { item: 'Offboarding checklist and access revocation evidence', context: 'Validates timely removal of access and return of organisational assets when personnel cease engagement' },
    { item: 'Contractor and third-party personnel security agreements', context: 'Demonstrates equivalent personnel security controls applied to non-employees' },
  ],
};

/**
 * Generate CSQ Submission Mapping from DISP Gap Analysis output.
 * Groups by CSQ domain, deduplicates controls mapping to the same category.
 */
export const generateCSQMapping = (dispGapAnalysis) => {
  if (!dispGapAnalysis || dispGapAnalysis.length === 0) return [];

  // Flatten all controls with their DISP domain
  const allControls = [];
  dispGapAnalysis.forEach(domain => {
    domain.controls.forEach(ctrl => {
      allControls.push({ ...ctrl, disp_domain: domain.domain_id });
    });
  });

  // Group by CSQ domain + category for deduplication
  const csqBuckets = {};
  let guidanceIndex = 0;

  allControls.forEach(ctrl => {
    const csqDomain = CSQ_DOMAIN_MAP[ctrl.disp_domain] || 'ICT Security';
    const csqCategory = CSQ_CATEGORY_MAP[ctrl.group_id] || 'General';
    const bucketKey = `${csqDomain}::${csqCategory}`;

    if (!csqBuckets[bucketKey]) {
      csqBuckets[bucketKey] = {
        csq_domain: csqDomain,
        csq_category: csqCategory,
        controls: [],
        worst_status: 'compliant',
        has_gating: false,
      };
    }

    const bucket = csqBuckets[bucketKey];
    bucket.controls.push(ctrl);

    // Track worst status
    if (ctrl.compliance_status === 'non_compliant') bucket.worst_status = 'non_compliant';
    else if (ctrl.compliance_status === 'partially_compliant' && bucket.worst_status !== 'non_compliant') bucket.worst_status = 'partially_compliant';

    if (ctrl.is_gating) bucket.has_gating = true;
  });

  // Build CSQ mapping blocks
  const csqDomainBuckets = {};

  Object.values(csqBuckets).forEach(bucket => {
    const status = bucket.worst_status;
    const guidancePool = CSQ_GUIDANCE[status] || CSQ_GUIDANCE.non_compliant;
    const guidance = guidancePool[guidanceIndex % guidancePool.length];
    guidanceIndex++;

    // Merge evidence from all controls, deduplicate
    const evidenceSet = new Set();
    bucket.controls.forEach(ctrl => {
      (ctrl.evidence_required || []).forEach(e => evidenceSet.add(e));
    });

    // Build combined control name
    const controlNames = bucket.controls.map(c => {
      const name = c.short_name.replace(/^CRITICAL:\s*/, '');
      return name;
    });
    const combinedName = controlNames.length === 1 ? controlNames[0] : controlNames.join(' + ');

    const responseDirection = status === 'non_compliant' ? 'No' : status === 'partially_compliant' ? 'Partial' : 'Yes';

    const block = {
      combined_name: combinedName,
      csq_category: bucket.csq_category,
      compliance_status: status,
      response_direction: responseDirection,
      submission_guidance: guidance,
      evidence_required: [...evidenceSet],
      review_risk: CSQ_REVIEW_RISK[status],
      positioning_tip: CSQ_POSITIONING_TIP[status],
      has_gating: bucket.has_gating,
      control_count: bucket.controls.length,
      // Execution layer: control-specific CSQ response
      csq_response_text: (CSQ_RESPONSE_SPECIFIC[bucket.csq_category] || {})[status] ||
        (status === 'compliant' ? 'The organisation has implemented and operationalised controls in this area with consistent enforcement and audit evidence.' :
         status === 'partially_compliant' ? 'The organisation has partially implemented controls; however, coverage is not yet consistent and evidence is being developed.' :
         'Controls are not yet implemented to the level required for CSQ submission. A remediation program is underway.'),
      // Evidence with Defence context
      supporting_evidence: CSQ_EVIDENCE_WITH_CONTEXT[bucket.csq_category] || [...evidenceSet].slice(0, 5).map(e => ({ item: e, context: '' })),
      // Minimum viable submission path
      minimum_viable_path: (status !== 'compliant' && CSQ_MINIMUM_PATH[bucket.csq_category]) || [],
      // Submission decision
      submission_decision: status === 'non_compliant'
        ? 'Recommendation: Do not submit this section in its current state. Remediation and initial evidence generation are required prior to CSQ submission.'
        : status === 'partially_compliant'
        ? 'Recommendation: Submission may proceed with clear explanation of current gaps and defined remediation timelines; expect Defence clarification.'
        : 'Recommendation: Suitable for submission with supporting evidence attached.',
      group_ids: bucket.controls.map(c => c.group_id),
    };

    if (!csqDomainBuckets[bucket.csq_domain]) {
      csqDomainBuckets[bucket.csq_domain] = { label: bucket.csq_domain, blocks: [] };
    }
    csqDomainBuckets[bucket.csq_domain].blocks.push(block);
  });

  // Build ordered domains array
  const domains = CSQ_DOMAIN_ORDER
    .filter(d => csqDomainBuckets[d])
    .map(d => ({ domain_label: d, blocks: csqDomainBuckets[d].blocks }));

  // Calculate submission confidence
  const allBlocks = Object.values(csqDomainBuckets).flatMap(d => d.blocks);
  const noCount = allBlocks.filter(b => b.response_direction === 'No').length;
  const partialCount = allBlocks.filter(b => b.response_direction === 'Partial').length;
  const yesCount = allBlocks.filter(b => b.response_direction === 'Yes').length;
  const hasCriticalNo = allBlocks.some(b => b.response_direction === 'No' && b.has_gating);

  let submissionConfidence;
  let confidenceExplanation;
  if (hasCriticalNo || noCount >= 3) {
    submissionConfidence = 'LOW';
    confidenceExplanation = 'Based on current control gaps, successful CSQ submission is unlikely without remediation.';
  } else if (noCount > 0 || partialCount >= 2) {
    submissionConfidence = 'MODERATE';
    confidenceExplanation = 'Submission is feasible; however, Defence will likely request clarification before acceptance.';
  } else {
    submissionConfidence = 'HIGH';
    confidenceExplanation = 'Organisation is well-positioned for CSQ submission, subject to evidence completeness.';
  }

  // Generate overall CSQ submission insight
  const nonCompliantCategories = allBlocks.filter(b => b.response_direction === 'No').map(b => b.csq_category.toLowerCase());
  const partialCategories = allBlocks.filter(b => b.response_direction === 'Partial').map(b => b.csq_category.toLowerCase());

  let overallInsight;
  if (noCount >= 4) {
    overallInsight = `The primary barrier to successful CSQ submission is the lack of centrally enforced control layers across ${nonCompliantCategories.slice(0, 3).join(', ')} and related domains. While individual controls are being implemented, the absence of consistent enforcement and audit-ready evidence limits the organisation's ability to demonstrate compliance across all in-scope systems. Addressing these systemic gaps will significantly improve submission viability and reduce the likelihood of Defence clarification requests.`;
  } else if (noCount > 0) {
    overallInsight = `The organisation demonstrates partial readiness across several CSQ domains; however, critical gaps in ${nonCompliantCategories.join(' and ')} must be addressed before submission. The lack of consistent enforcement and documented evidence in these areas will attract Defence scrutiny and will likely result in submission rejection or material delays.`;
  } else if (partialCount > 0) {
    overallInsight = `The organisation is approaching submission readiness with controls partially implemented across ${partialCategories.join(', ')}. The primary remaining task is to standardise enforcement, close evidence gaps, and ensure all controls can be demonstrated as consistently operational. With targeted effort, submission within a short timeframe is achievable.`;
  } else {
    overallInsight = 'The organisation demonstrates strong control maturity across all assessed CSQ domains. Subject to evidence completeness and currency, the organisation is well-positioned for CSQ submission.';
  }

  // Generate global minimum submission path (top 3-5 actions from gating/highest impact)
  const gatingBlocks = allBlocks.filter(b => b.has_gating && b.response_direction !== 'Yes');
  const nonCompliantBlocks = allBlocks.filter(b => b.response_direction === 'No' && !b.has_gating);
  const globalMinPath = [];

  // Prioritise gating controls first, then non-compliant
  [...gatingBlocks, ...nonCompliantBlocks].forEach(block => {
    (block.minimum_viable_path || []).forEach(action => {
      if (globalMinPath.length < 5 && !globalMinPath.includes(action)) {
        globalMinPath.push(action);
      }
    });
  });

  return {
    domains,
    submission_confidence: submissionConfidence,
    confidence_explanation: confidenceExplanation,
    summary: { no: noCount, partial: partialCount, yes: yesCount, total: allBlocks.length },
    overall_insight: overallInsight,
    global_minimum_path: globalMinPath,
  };
};

/**
 * Build section answers in schema format
 * @param {Object} answers - All answers keyed by field_id
 * @returns {Object} Section answers
 */
const buildSectionAnswers = (answers) => {
  const sections = {};

  Object.entries(QUESTION_CONFIG).forEach(([sectionId, section]) => {
    if (sectionId === 'engagement') return; // Skip engagement (handled separately)

    const sectionData = {};
    section.questions.forEach(question => {
      const answer = answers[question.field_id];
      if (answer) {
        sectionData[question.field_id] = {
          status: answer.status || 'unknown',
          score: getAnswerScore(answer, question),
          answer: answer.answer,
          notes: answer.notes || '',
          recommended_actions: question.recommended_actions || [],
          recommended_components: question.recommended_components || [],
          priority: answer.priority || null
        };
      }
    });

    if (Object.keys(sectionData).length > 0) {
      // Personnel Security domain ships with Accelerator/CSQ-ready export structure
      if (sectionId === 'personnel_security') {
        sections[sectionId] = {
          source: 'discovery',
          csq_mapping: [
            { defenciq_id: 'PER-01', csq_id: 'PS-01' },
            { defenciq_id: 'PER-02', csq_id: 'PS-02' },
            { defenciq_id: 'PER-03', csq_id: 'PS-03' },
            { defenciq_id: 'PER-04', csq_id: 'PS-04' },
            { defenciq_id: 'PER-05', csq_id: 'PS-05' },
            { defenciq_id: 'PER-06', csq_id: 'PS-06' },
            { defenciq_id: 'PER-07', csq_id: 'PS-07' },
          ],
          answers: sectionData,
        };
      } else {
        sections[sectionId] = sectionData;
      }
    }
  });

  return sections;
};

/**
 * Export report as JSON string
 * @param {Object} report - Generated report
 * @returns {string} JSON string
 */
export const exportReportAsJSON = (report) => {
  return JSON.stringify(report, null, 2);
};

/**
 * Get report filename
 * @param {Object} engagement - Engagement details
 * @returns {string} Filename
 */
export const getReportFilename = (engagement) => {
  const clientName = (engagement.client_name || 'unknown')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '-')
    .substring(0, 30);
  const date = new Date().toISOString().split('T')[0];
  return `defenciq-discovery-${clientName}-${date}.json`;
};

// ============================================================================
// ML2 IMPLEMENTATION BLUEPRINT GENERATOR
// ============================================================================

function buildCapabilityModel(weightedScore, evidenceMultiplier, policyMultiplier) {
  const capabilities = [
    { name: 'Enforcement', description: 'Technical controls that actively prevent or restrict threats - MFA, device compliance, application whitelisting, access restrictions.' },
    { name: 'Visibility', description: 'Ability to observe the current state of controls, configurations, and coverage across the environment.' },
    { name: 'Monitoring', description: 'Continuous detection of anomalies, control drift, and security events with appropriate alerting.' },
    { name: 'Evidence', description: 'Systematic generation and retention of audit-ready artefacts that demonstrate control effectiveness to assessors.' }
  ];

  const emphasis = [];
  if (weightedScore < 40) {
    emphasis.push('Foundational enforcement and visibility gaps must be addressed before compliance monitoring or evidence generation can be effective.');
  }
  if (evidenceMultiplier < 0.7) {
    emphasis.push('Evidence maturity is critically low. Without structured evidence generation, the organisation cannot demonstrate control effectiveness to Defence assessors, regardless of technical implementation.');
  }
  if (policyMultiplier < 0.7) {
    emphasis.push('Technical enforcement is insufficient. Policies exist without corresponding technical controls, creating a gap between documented intent and operational reality.');
  }

  let narrative;
  if (weightedScore < 25) {
    narrative = 'The organisation lacks foundational capabilities across all four pillars. Priority must be given to establishing basic enforcement controls before progressing to monitoring and evidence.';
  } else if (weightedScore < 40) {
    narrative = 'Foundational gaps exist across enforcement and visibility. The organisation has partial capabilities but lacks the maturity required for DISP submission. A structured uplift across all four capabilities is required.';
  } else if (weightedScore < 65) {
    narrative = 'The organisation has developing capabilities but requires targeted uplift in specific domains. Focus should be on closing enforcement gaps and establishing systematic evidence generation.';
  } else if (weightedScore < 85) {
    narrative = 'Capabilities are moderately mature. Remaining work centres on validation, evidence formalisation, and ensuring controls meet the operational effectiveness standard expected by Defence.';
  } else {
    narrative = 'Capabilities are well-established. Focus should shift to evidence packaging, CSQ preparation, and continuous compliance monitoring to maintain readiness.';
  }

  return { capabilities, emphasis, narrative };
}

function buildReferenceArchitecture(stackByLayer, recommendedComponents) {
  const layerDefs = {
    enforcement_layer: {
      name: 'Control Enforcement Layer',
      description: 'Active controls that prevent or restrict threats',
      toolMappings: {
        microsoft_entra_id: 'Identity enforcement and access management',
        conditional_access: 'Context-aware access policies',
        microsoft_intune: 'Device compliance and configuration management',
        microsoft_defender_for_endpoint: 'Endpoint detection and response',
        wdac_or_applocker: 'Application whitelisting enforcement',
        privileged_access_management: 'Privileged access lifecycle control',
        backup_platform: 'Data resilience and recovery'
      }
    },
    validation_layer: {
      name: 'Validation & Assurance Layer',
      description: 'Tools that verify control effectiveness and identify weaknesses',
      toolMappings: {
        tenable: 'Continuous vulnerability assessment',
        qualys: 'Vulnerability scanning and compliance checks'
      },
      criticalGap: 'vulnerability scanner'
    },
    monitoring_layer: {
      name: 'Monitoring & Detection Layer',
      description: 'Continuous observation, alerting, and anomaly detection',
      toolMappings: {
        microsoft_sentinel: 'SIEM - centralised log aggregation and threat detection'
      },
      criticalGap: 'SIEM'
    },
    compliance_layer: {
      name: 'Compliance & Evidence Layer',
      description: 'Compliance tracking, evidence collection, and audit preparation',
      toolMappings: {
        drata: 'Compliance automation and control mapping',
        remediation_tracker: 'Gap tracking and remediation workflow',
        evidence_pack_generator: 'Automated evidence packaging',
        disp_dashboard: 'DISP submission tracking and CSQ alignment'
      }
    }
  };

  const layers = {};

  Object.entries(layerDefs).forEach(([layerId, def]) => {
    const tools = [];
    const layerComps = stackByLayer[layerId]?.components || [];
    layerComps.forEach(comp => {
      tools.push({
        id: comp.id,
        label: comp.label,
        role: def.toolMappings[comp.id] || comp.label
      });
    });

    const gaps = [];
    if (tools.length === 0 && def.criticalGap) {
      gaps.push(`No ${def.criticalGap} identified in the recommended stack. This is a significant gap for ML2 compliance.`);
    }

    layers[layerId] = {
      name: def.name,
      description: def.description,
      tools,
      gaps
    };
  });

  return { layers };
}

function buildContinuousCompliance(weightedScore, environmentType, recommendedComponents) {
  let vmSizing;
  if (environmentType === 'managed_mature') {
    vmSizing = { label: 'Scalable VM', spec: '4 vCPU, 16 GB RAM, 200 GB storage (scalable)', reason: 'Mature environment requires scalable deployment to handle volume of integrations and evidence.' };
  } else if (environmentType === 'structured_non_compliant') {
    vmSizing = { label: 'Medium VM', spec: '2 vCPU, 8 GB RAM, 100 GB storage', reason: 'Structured environment with established tooling requiring standard integration capacity.' };
  } else {
    vmSizing = { label: 'Standard VM', spec: '2 vCPU, 4 GB RAM, 80 GB storage', reason: 'Smaller or fragmented environment with limited initial integration points.' };
  }

  const integrationPoints = [];
  const compIds = new Set(recommendedComponents.map(c => c.id));
  if (compIds.has('microsoft_entra_id') || compIds.has('conditional_access')) {
    integrationPoints.push({ system: 'Identity (Entra ID / Conditional Access)', purpose: 'MFA validation, access policy enforcement verification' });
  }
  if (compIds.has('microsoft_intune') || compIds.has('microsoft_defender_for_endpoint')) {
    integrationPoints.push({ system: 'Endpoint Management (Intune / Defender)', purpose: 'Device compliance, patch status, endpoint protection state' });
  }
  if (compIds.has('microsoft_sentinel')) {
    integrationPoints.push({ system: 'SIEM (Microsoft Sentinel)', purpose: 'Log ingestion validation, alert correlation, monitoring coverage' });
  }
  if (compIds.has('tenable') || compIds.has('qualys')) {
    integrationPoints.push({ system: 'Vulnerability Management (Tenable / Qualys)', purpose: 'Vulnerability state, scan coverage, remediation tracking' });
  }
  if (compIds.has('backup_platform')) {
    integrationPoints.push({ system: 'Backup Platform', purpose: 'Backup schedule verification, recovery testing evidence' });
  }
  if (integrationPoints.length === 0) {
    integrationPoints.push({ system: 'Core infrastructure', purpose: 'Initial data ingestion and baseline validation' });
  }

  const functionalRole = [
    'Continuously validates control effectiveness against Essential Eight ML2 requirements',
    'Detects configuration drift and control degradation',
    'Produces audit-ready evidence mapped to CSQ response requirements',
    'Maps implemented controls to Essential Eight ML2 maturity criteria',
    'Connectors should first be established to platforms already present in the environment before introducing net-new tooling'
  ];

  return {
    vmSizing,
    integrationPoints,
    functionalRole,
    isCritical: weightedScore < 30,
    criticalNote: weightedScore < 30
      ? 'At the current maturity level, this platform is a CRITICAL dependency for achieving DISP readiness. Without continuous compliance validation, the organisation cannot demonstrate sustained control effectiveness.'
      : null
  };
}

function buildEvidenceModel(priorityGaps, evidenceMultiplier) {
  const mappings = [];
  const gapDomains = new Set(priorityGaps.map(g => g.domain));

  if (gapDomains.has('identity_and_mfa')) {
    mappings.push({ control: 'MFA & Identity', source: 'Entra ID sign-in logs, conditional access audit logs', output: 'MFA enforcement evidence, access policy coverage reports' });
  }
  if (gapDomains.has('patch_management')) {
    mappings.push({ control: 'Patch Management', source: 'Vulnerability scanner reports, endpoint management compliance data', output: 'Patch currency reports, vulnerability remediation timelines' });
  }
  if (gapDomains.has('logging_and_monitoring')) {
    mappings.push({ control: 'Logging & Monitoring', source: 'SIEM ingestion logs, alert configuration exports', output: 'Log coverage reports, alert rule documentation' });
  }
  if (gapDomains.has('backups')) {
    mappings.push({ control: 'Backups', source: 'Backup platform job logs, recovery test records', output: 'Backup schedule evidence, recovery validation reports' });
  }
  if (gapDomains.has('endpoint_management')) {
    mappings.push({ control: 'Endpoint Management', source: 'Intune compliance reports, device configuration baselines', output: 'Device compliance evidence, hardening baseline reports' });
  }
  if (gapDomains.has('application_control')) {
    mappings.push({ control: 'Application Control', source: 'WDAC/AppLocker policy exports, enforcement logs', output: 'Application whitelisting evidence, policy enforcement reports' });
  }
  if (gapDomains.has('privileged_access')) {
    mappings.push({ control: 'Privileged Access', source: 'PAM session logs, privilege escalation audit trails', output: 'Privileged account inventory, access review evidence' });
  }
  if (gapDomains.has('vulnerability_management')) {
    mappings.push({ control: 'Vulnerability Management', source: 'Vulnerability scan results, remediation tracking data', output: 'Vulnerability posture reports, scan coverage evidence' });
  }

  const isCritical = evidenceMultiplier < 0.6;

  return {
    mappings,
    isCritical,
    criticalNote: isCritical
      ? 'Current evidence maturity is critically insufficient and must be centralised. Without a structured evidence management approach, DISP submission will not pass Defence scrutiny.'
      : null,
    outputs: [
      'Control-level evidence packs aligned to each Essential Eight mitigation strategy',
      'CSQ-ready artefacts mapped to specific questionnaire sections',
      'Audit trails demonstrating operational effectiveness over time'
    ]
  };
}

function buildImplementationPhases(priorityGaps) {
  const enforcementDomains = new Set(['identity_and_mfa', 'application_control', 'endpoint_management', 'privileged_access']);
  const monitoringDomains = new Set(['logging_and_monitoring', 'vulnerability_management', 'patch_management']);

  const enforcementGaps = priorityGaps.filter(g =>
    enforcementDomains.has(g.domain) && (g.priority === 'critical' || g.priority === 'high')
  );
  const monitoringGaps = priorityGaps.filter(g =>
    monitoringDomains.has(g.domain) && (g.priority === 'critical' || g.priority === 'high' || g.priority === 'medium')
  );

  return {
    phase0: {
      name: 'Phase 0 - Confirm and Baseline Existing Tools',
      description: 'Confirm status of all declared tooling, validate configuration, and establish integration baseline',
      items: [
        'Validate declared tooling status against operational evidence',
        'Deploy Continuous Compliance Platform VM in client environment',
        'Configure integration connectors for identity, endpoint, and logging systems',
        'Validate data ingestion from all connected sources',
        'Establish baseline control mapping against Essential Eight ML2'
      ],
      alwaysPresent: true
    },
    phase1: {
      name: 'Phase 1 - Fix Ineffective or Partially Deployed Enforcement Tooling',
      description: 'Address critical enforcement gaps and uplift partially deployed tools to operational effectiveness',
      items: enforcementGaps.slice(0, 8).map(g => g.gap),
      isEmpty: enforcementGaps.length === 0,
      emptyNote: 'No critical enforcement gaps identified. Proceed to validation phase.'
    },
    phase2: {
      name: 'Phase 2 - Introduce Missing Required Platforms',
      description: 'Deploy net-new required components for monitoring, vulnerability management, and patch compliance',
      items: monitoringGaps.slice(0, 8).map(g => g.gap),
      isEmpty: monitoringGaps.length === 0,
      emptyNote: 'No critical monitoring gaps identified. Proceed to evidence phase.'
    },
    phase3: {
      name: 'Phase 3 - Automate Evidence and CSQ Readiness Workflows',
      description: 'Generate audit-ready evidence, prepare CSQ responses, and validate submission readiness',
      items: [
        'Generate control-level evidence packs for each Essential Eight domain',
        'Map evidence to CSQ response requirements',
        'Conduct mock audit validation against Defence expectations',
        'Prepare and review CSQ responses with supporting artefacts',
        'Validate sustained control effectiveness over assessment period'
      ],
      alwaysPresent: true
    }
  };
}

export const generateImplementationBlueprint = ({
  weightedScore,
  scoreBand,
  environmentType,
  evidenceMultiplier,
  policyMultiplier,
  domainScores,
  priorityGaps,
  stackByLayer,
  recommendedComponents
}) => {
  const capabilityModel = buildCapabilityModel(weightedScore, evidenceMultiplier, policyMultiplier);
  const referenceArchitecture = buildReferenceArchitecture(stackByLayer, recommendedComponents);
  const continuousCompliance = buildContinuousCompliance(weightedScore, environmentType, recommendedComponents);
  const evidenceModel = buildEvidenceModel(priorityGaps, evidenceMultiplier);
  const phases = buildImplementationPhases(priorityGaps);

  return {
    capabilityModel,
    referenceArchitecture,
    continuousCompliance,
    evidenceModel,
    phases,
    severeGapWarning: weightedScore < 25
      ? 'A structured uplift program is required before DISP submission is viable.'
      : null
  };
};

/**
 * Transform a gap question into plain executive English
 */
const toExecutiveGapText = (gap) => {
  const gapText = gap?.gap || gap?.prompt || '';
  const translations = {
    'mfa': {
      'privileged': 'Privileged accounts are not fully protected with multi-factor authentication',
      'all users': 'Multi-factor authentication is not enforced across the organisation',
      'all': 'Multi-factor authentication is not enforced across the organisation'
    },
    'allow-listing': 'Application control is not fully enforced',
    'allow listing': 'Application control is not fully enforced',
    'whitelisting': 'Application control is not fully enforced',
    'remotely configured or wiped': 'Device management controls are not fully in place',
    'remote config': 'Device management controls are not fully in place',
    'application execution monitored': 'Security monitoring of software activity is incomplete',
    'critical security patches': 'Critical patching timeframes are not well defined or enforced',
    'unpatched systems': 'The organisation does not yet have strong visibility of unpatched systems',
    'admin activity logged': 'Privileged user activity is not consistently monitored and reviewed',
    'separate admin': 'Separate administrator accounts are not consistently used',
    'centralised logging': 'No centralised security logging is in place',
    'alerts actively monitored': 'Security alerts are not being actively monitored',
    'vulnerability scanning': 'Vulnerability scanning is not implemented',
    'device compliance': 'Device compliance policies are not enforced',
    'backup': 'Backup protection needs strengthening',
    'immutable': 'Backups are not adequately protected against tampering',
    'restore test': 'Backup recovery has not been tested recently',
    'conditional access': 'Context-aware access controls are not configured',
    'evidence': 'Audit evidence gathering processes need improvement',
    'compliance management': 'Compliance management processes are not mature',
    'annual security report': 'Annual Security Report (ASR) process is not formally established',
    'asr': 'Annual Security Report (ASR) process is not formally established',
    'facilities, sites': 'DISP facility and registration scope is not formally documented',
    'registration scope': 'DISP facility and registration scope is not formally documented',
    'pre-employment screening': 'Pre-employment screening is not aligned to AS4811',
    'baseline vetting': 'Baseline vetting of personnel accessing Defence systems is not enforced',
    'security clearances': 'Security clearance sponsorship and tracking is not formally established',
    'insider threat': 'Insider threat awareness training is not formally implemented',
    'offboarding': 'Personnel offboarding and access revocation is not formally controlled'
  };

  const lower = gapText.toLowerCase();
  // Check MFA with sub-categories
  if (lower.includes('mfa') || lower.includes('multi-factor')) {
    if (lower.includes('privileged')) return translations.mfa.privileged;
    return translations.mfa.all;
  }
  // Check other translations
  for (const [key, val] of Object.entries(translations)) {
    if (key === 'mfa') continue;
    if (lower.includes(key)) return typeof val === 'string' ? val : val;
  }
  // Fallback: clean the question text into a statement
  return gapText
    .replace(/^Is\s+/i, '')
    .replace(/^Are\s+/i, '')
    .replace(/^Does\s+/i, '')
    .replace(/^Can\s+/i, '')
    .replace(/^What is the\s+/i, '')
    .replace(/\?$/, '')
    .replace(/^(.)/,  (m) => m.toUpperCase()) + ' needs attention';
};

/**
 * Build payload for Executive Snapshot PDF
 * Reuses existing computed results - no new scoring
 */
export const buildExecutiveSnapshotPayload = (engagement, report) => {
  const derived = report.derived_outputs || {};
  const metadata = report._report_metadata || {};
  const brief = metadata.executive_brief || {};
  const hybrid = metadata.hybrid_report || {};
  const hybridSnap = hybrid.executive_snapshot || {};
  const hybridCommercial = hybrid.commercial_impact || {};
  const hybridGating = hybrid.gating_analysis || {};
  const hybridEvidence = hybrid.evidence_readiness || {};
  const hybridDecision = hybrid.decision_view || {};

  const weightedScore = metadata.enhanced_scores?.finalScore || derived.overall_ml2_readiness_score || 0;
  const scoreBand = metadata.enhanced_scores?.scoreBand?.label || derived.overall_status || 'Unknown';
  const auditRisk = metadata.enhanced_scores?.auditFailureLikelihood || 'Unknown';
  const priorityGaps = derived.priority_gaps || [];
  const domainFindings = metadata.domain_findings || [];

  // Readiness status from actual DISP readiness assessment
  const dispStatus = metadata.disp_readiness?.submission_status || 'NOT_READY';
  let readinessStatus;
  let readinessLabel;
  if (dispStatus === 'READY_FOR_SUBMISSION') {
    readinessStatus = 'Approaching DISP Submission Readiness';
    readinessLabel = 'Ready';
  } else if (dispStatus === 'PARTIALLY_READY') {
    readinessStatus = 'Partially ready for DISP submission';
    readinessLabel = 'Partially Ready';
  } else {
    readinessStatus = 'Not ready for DISP submission';
    readinessLabel = 'Not Ready';
  }

  // Time to readiness — prefer hybrid value (more granular bands)
  const timeToReadiness = hybridSnap.estimated_time_to_readiness ||
    (weightedScore >= 80 ? 'Near-ready' : weightedScore >= 55 ? '3-6 weeks' : '6-10 weeks');

  // Timeline confidence (Low/Medium/High) - inverse of risk: more gaps + lower evidence = lower confidence
  const evidenceMaturity = hybridEvidence.evidence_maturity_score || 'Low';
  const gatingFails = hybridGating.summary?.fail || 0;
  const gatingPartials = hybridGating.summary?.partial || 0;
  let timeline_confidence;
  if (gatingFails === 0 && gatingPartials <= 1 && evidenceMaturity === 'High' && weightedScore >= 75) {
    timeline_confidence = 'High';
  } else if (gatingFails > 3 || (gatingPartials > 5 && evidenceMaturity === 'Low') || weightedScore < 35) {
    timeline_confidence = 'Low';
  } else {
    timeline_confidence = 'Medium';
  }

  // Evidence Readiness signal — single-line rating + 1-2 sentence advisory explanation.
  // Source: hybrid_report.evidence_readiness.evidence_maturity_score (already gating-aware downgraded).
  let evidence_readiness_explanation;
  if (evidenceMaturity === 'High') {
    evidence_readiness_explanation = 'Audit-ready evidence is centrally maintained and traceable to controls. The organisation is positioned to defend a CSQ submission against assessor inspection.';
  } else if (evidenceMaturity === 'Medium') {
    evidence_readiness_explanation = 'Evidence exists but is fragmented and not consistently linked to controls. This will weaken the submission package and trigger Defence clarification requests during validation.';
  } else {
    evidence_readiness_explanation = 'Audit-ready evidence is not centralised or consistently aligned to controls. This will prevent a defensible CSQ submission even where controls are partially implemented.';
  }
  const evidence_readiness = {
    level: evidenceMaturity,
    explanation: evidence_readiness_explanation,
  };

  // Recommended service - prefer the rendered label; fall back to title-casing the raw key
  const rawTier = derived.recommended_service_tier;
  const rawTierLabel = (rawTier || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const recommendedService = derived.service_tier_info?.label
    || derived.recommended_service_tier_info?.label
    || rawTierLabel
    || 'Structured Uplift Program';

  // Weakest and strongest domains
  const sorted = [...domainFindings].sort((a, b) => a.score - b.score);
  const weakest = sorted.slice(0, 3).map(d => ({ name: d.domain_name, score: d.score }));
  const strongest = sorted.slice(-3).reverse().map(d => ({ name: d.domain_name, score: d.score }));

  // Submission Blockers — derived from gating + weakest domains + evidence maturity (max 4)
  const submission_blockers = [];
  if (gatingFails > 0 && gatingPartials > 0) {
    submission_blockers.push(`${gatingFails} gating control${gatingFails > 1 ? 's are' : ' is'} fully failed and ${gatingPartials} ${gatingPartials > 1 ? 'are' : 'is'} only partially implemented`);
  } else if (gatingFails > 0) {
    submission_blockers.push(`${gatingFails} gating control${gatingFails > 1 ? 's are' : ' is'} fully failed - hard block on DISP submission`);
  } else if (gatingPartials > 0) {
    submission_blockers.push(`${gatingPartials} gating control${gatingPartials > 1 ? 's are' : ' is'} only partially implemented and not audit-ready`);
  }
  if (weakest.length > 0 && weakest[0].score < 50) {
    const weakDomainNames = weakest.filter(d => d.score < 50).slice(0, 3).map(d => d.name).join(', ').toLowerCase();
    submission_blockers.push(`${weakDomainNames} controls lack consistent enforcement across in-scope systems`);
  }
  if (evidenceMaturity === 'Low') {
    submission_blockers.push('Evidence is not centralised or defensible for CSQ validation');
  } else if (evidenceMaturity === 'Medium') {
    submission_blockers.push('Evidence is fragmented and lacks consistent audit traceability');
  }
  if (submission_blockers.length < 4 && (hybrid.csq_enhancements || []).filter(c => c.submission_risk === 'High').length > 0) {
    submission_blockers.push('Multiple CSQ categories cannot be evidenced in current form');
  }
  // Trim to 4
  const trimmed_blockers = submission_blockers.slice(0, 4);

  // Gating Control Status (single line + insight)
  const gatingPass = hybridGating.summary?.pass || 0;
  const gating_status_line = `${gatingPass} Passed | ${gatingPartials} Partial | ${gatingFails} Failed`;
  let gating_insight;
  if (gatingFails > 0 && gatingPartials > 0) {
    gating_insight = `${gatingFails} gating control${gatingFails > 1 ? 's are' : ' is'} fully failed and ${gatingPartials} ${gatingPartials > 1 ? 'are' : 'is'} only partially implemented. Both conditions block a defensible DISP submission.`;
  } else if (gatingFails > 0) {
    gating_insight = `${gatingFails} gating control${gatingFails > 1 ? 's are' : ' is'} fully failed. Defence will reject the CSQ until each is implemented to a demonstrable standard.`;
  } else if (gatingPartials > 0) {
    gating_insight = `While no controls are fully failed, partial implementation across ${gatingPartials} gating control${gatingPartials > 1 ? 's' : ''} is sufficient to prevent a defensible DISP submission.`;
  } else {
    gating_insight = `All ${gatingPass} gating controls are fully implemented - structural prerequisites for submission are cleared.`;
  }

  // Commercial Impact (replaces Business Risk) — derive from hybrid_report.commercial_impact
  const commercial_impact = [
    `Estimated delay to Defence contract eligibility: ${hybridCommercial.estimated_contract_delay || timeToReadiness}`,
    `${hybridCommercial.csq_rejection_likelihood || 'High'} likelihood of CSQ rejection if submitted in current state`,
    hybridCommercial.resubmission_risk === 'Low'
      ? 'Resubmission risk is contained with current evidence posture'
      : `Resubmission effort will likely be required without remediation (${hybridCommercial.resubmission_risk || 'High'} risk)`,
  ];

  // Top blockers (legacy field, retained for back-compat with consumers that read `top_blockers`)
  const critHighGaps = priorityGaps.filter(g => g.priority === 'critical' || g.priority === 'high');
  const topBlockers = critHighGaps.slice(0, 5).map(g => toExecutiveGapText(g));

  // ====================================================================
  // PRIORITY ACTIONS — restructured into 2 non-overlapping groups
  // (1) Critical First Steps - gating-related controls only (max 5)
  // (2) Next Phase (ML2 Uplift) - high-priority non-gating only (max 5)
  // ====================================================================
  const gatingControls = metadata.gating_control_details || [];
  const failedOrPartialGating = gatingControls.filter(c => c.score < 2);
  const gatingFieldIds = new Set(failedOrPartialGating.map(c => c.field_id));

  const promptToAction = (prompt) => {
    const p = (prompt || '').toLowerCase();
    if (p.includes('mfa') && p.includes('privileged')) return 'Enforce MFA for all privileged users';
    if (p.includes('mfa')) return 'Enforce MFA across the organisation';
    if (p.includes('allow-listing') || p.includes('allow listing') || p.includes('application control')) return 'Implement application control enforcement';
    if (p.includes('logging')) return 'Implement centralised security logging';
    if (p.includes('backup') || p.includes('immutable')) return 'Ensure backups are immutable and restore-tested';
    if (p.includes('admin') && p.includes('separate')) return 'Establish separate administrator accounts';
    if (p.includes('patch')) return 'Centralise patch management with SLA enforcement';
    if (p.includes('macros')) return 'Block office macros from internet sources';
    if (p.includes('compliance') && p.includes('device')) return 'Enforce device compliance policies';
    if (p.includes('central device')) return 'Deploy central device management';
    return prompt?.replace(/^Is\s+/i, 'Implement ').replace(/^Are\s+/i, 'Implement ').replace(/\?$/, '') || 'Address critical control gap';
  };

  const criticalActions = [];
  const seenCritical = new Set();
  failedOrPartialGating.slice(0, 8).forEach(c => {
    const action = promptToAction(c.prompt);
    if (!seenCritical.has(action) && criticalActions.length < 5) {
      criticalActions.push(action);
      seenCritical.add(action);
    }
  });

  const upliftActions = [];
  const seenUplift = new Set(seenCritical);
  priorityGaps
    .filter(g => g.priority === 'high' && !gatingFieldIds.has(g.field_id))
    .forEach(g => {
      const action = toExecutiveGapText(g);
      if (!seenUplift.has(action) && upliftActions.length < 5) {
        upliftActions.push(action);
        seenUplift.add(action);
      }
    });

  // Risk sentence (kept for back-compat; tone upgraded)
  const riskSentences = {
    CRITICAL: 'Immediate remediation is required before submission is attempted.',
    HIGH: 'Submission will likely fail or be delayed without structured remediation.',
    MODERATE: 'Several issues will likely delay readiness if not addressed.',
    LOW: 'Current risk is comparatively low if controls are maintained.'
  };

  // DefencIQ value proposition — outcome-based, not marketing
  const defenciqValuePoints = [
    'We identify submission blockers, not just generic gaps',
    'We prioritise the controls that directly impact DISP approval',
    'We translate ML2 requirements into executable, scoped actions',
    'We produce audit-ready evidence aligned to CSQ validation',
    'We reduce rework, delays, and the risk of failed submissions',
  ];

  // Closing recommendation (tone upgraded)
  const closingRecommendation = (gatingFails > 0 || gatingPartials > 0)
    ? `Based on the assessment results, the organisation must close the gating control gaps and centralise audit-ready evidence before any DISP CSQ submission is attempted. The structured ${recommendedService.toLowerCase()} is the fastest path to a defensible position.`
    : `Based on the assessment results, the fastest path to a defensible DISP submission is a structured uplift program focused on closing remaining control gaps and packaging evidence to CSQ standards.`;

  // Engagement type - prefer the canonical label, fallback to title-casing
  const ENGAGEMENT_TYPE_LABELS = {
    discovery_only: 'Discovery Only',
    snapshot_assessment: 'Snapshot Assessment',
    managed_compliance_onboarding: 'Managed Compliance Onboarding',
    greenfield_ml2_build: 'Greenfield ML2 Build',
    disp_readiness_uplift: 'DISP Readiness Uplift',
  };
  const rawEngType = engagement.engagement_type || '';
  const engagementTypeLabel = rawEngType
    ? (ENGAGEMENT_TYPE_LABELS[rawEngType] || rawEngType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
    : '';

  return {
    title: 'DefencIQ DISP Readiness Executive Snapshot',
    client_name: engagement.client_name || 'Client',
    assessment_date: engagement.assessment_date || new Date().toISOString().split('T')[0],
    assessor_name: engagement.assessor_name || '',
    engagement_type: engagementTypeLabel,
    weighted_ml2_score: weightedScore,
    score_band: scoreBand,
    readiness_status: readinessStatus,
    readiness_label: readinessLabel,
    environment_type: derived.environment_type_info?.label || derived.environment_type || '',
    audit_failure_risk: auditRisk,
    time_to_readiness: timeToReadiness,
    timeline_confidence,
    submission_decision: hybridDecision.final_recommendation || null,
    recommended_service: recommendedService,
    priority_gap_count: priorityGaps.length,
    top_blockers: topBlockers,
    submission_blockers: trimmed_blockers,
    gating_status_line,
    gating_insight,
    gating_summary: { pass: gatingPass, partial: gatingPartials, fail: gatingFails },
    evidence_readiness,
    // Independent Evidence Readiness Assessment (Discovery-level, % + band).
    // Shown alongside ML2 on the Executive Snapshot so boards see both dimensions.
    evidence_readiness_assessment: metadata.evidence_readiness_assessment || null,
    commercial_impact,
    business_consequences: commercial_impact, // back-compat alias
    risk_sentence: riskSentences[auditRisk] || riskSentences.MODERATE,
    priority_actions: {
      critical: criticalActions,
      uplift: upliftActions,
      // evidence group removed — content folded into uplift / commercial impact
      evidence: [],
    },
    strongest_domains: strongest,
    weakest_domains: weakest,
    defenciq_value_points: defenciqValuePoints,
    closing_recommendation: closingRecommendation
  };
};

export default {
  generatePriorityGaps,
  generateRoadmap,
  generateDomainFindings,
  generateExecutiveSummary,
  generateReport,
  exportReportAsJSON,
  getReportFilename,
  deriveDISPReadiness,
  generateImplementationBlueprint,
  buildExecutiveSnapshotPayload,
  generateDISPGapAnalysis,
  generateCSQMapping
};
